import {
  React,
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from 'react';
import api from '../../../../api';
import {
  Dropdown,
  OverlayTrigger,
  Popover,
  SplitButton,
  Modal,
  Form,
  Button,
  Row,
  Col,
  FormControl,
  Nav,
  ToggleButtonGroup,
  ToggleButton,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ChromePicker } from 'react-color';
import { Grid3x3, Grid3x2, Grid2x3, Grid2x2 } from './SvgIcons';
import ColorList from './ColorList';
import CustomInput from './CustomInput';
import { useTranslation } from 'react-i18next';
import TagNameField from './TagNameField';
import {
  MIXED,
  collectTargetEntries,
  computeCommonSettings,
  getByPath,
  isMixed,
  pickMajorityColor,
  resolveSettingValue,
  uniformColorInfo,
  updateNestedField,
  validateTagRename,
} from '../../../../utils/multiSelection';
import {
  TEXT_FIELD_PATHS,
  defaultHighlight,
  defaultUnderline,
  defaultStrikethrough,
  defaultIsActiveStates,
  defaultSelectedTextColorIndexes,
} from './textEntryDefaults';
import { defaultLegendIconObject } from './iconEntryDefaults';
import {
  checkUnsupportedGlyphs,
  fetchFontStyles,
  sortFontPreviewsBySearch,
} from '../../../../utils/fontHelpers';
import { fontCacheKey } from '../../../../utils/textLegend';
import { assignLegendsToLayout } from '../../../../utils/legendAssignment';
import { deepClone } from '../../../../utils/deepObjects';
import { rgbaToHex } from '../../../../utils/colorHelpers';

// The following objects are declared outside the component, as they are static (don't depend on any other variables) and don't change:
const TAG_NAME_KEYS = {
  empty: 'text-editor-component-name-empty',
  'too-long': 'text-editor-component-name-too-long',
  'already-in-use': 'text-editor-component-name-already-in-use',
}; // the toast for each reason validateTagRename (utils/multiSelection.js) refuses a tag name

const TEXT_COLOR_SETTINGS = [
  'fill',
  'stroke',
  'highlight',
  'underline',
  'strikethrough',
]; // the colour settings of a text entry, each with its colour list

const TextEditor = forwardRef(
  (
    {
      isActive,
      fonts,
      design,
      hasAssignedKeycapIDs,
      selectedLegend,
      setSelectedLegend,
      predefinedTagColors,
      updateDesign,
    },
    ref
  ) => {
    // We use forwardRef to allow the parent component (CustomizerScreen.js) to access functions or values inside TextEditor.
    // This is useful when the parent needs to directly call methods (like triggering an update) on the child.
    const { t } = useTranslation();

    const keycapData = design.keycapData;
    const {
      selectedTextIndex,
      tags,
      colors,
      selectedColorEntry,
      selectedTag,
      predefinedLegends: { id, os, configurationStyle, legendStyle },
    } = design.textEditor;
    const {
      selectedEditor,
      selectedKeycaps = [],
      isoEnterSelected,
      showGrid,
      showBoundingBox,
      // showPrintableArea, // uncomment together with the disabled Printable Area checkbox below
    } = design.settings;
    const [showPredefinedLegendsSettings, setShowPredefinedLegendsSettings] =
      useState(true); // State to control whether the settings panel is shown or collapsed
    const [screenWidthXL, setScreenWidthXL] = useState(
      window.innerWidth <= 1199
    ); // State to track whether the screen size is <= 1199px
    const [showTextEntries, setShowTextEntries] = useState(true); // State to control whether the settings panel is shown or collapsed
    const [screenWidthMD, setScreenWidthMD] = useState(
      window.innerWidth <= 767
    ); // State to track whether the screen size is <= 767px
    const [showTextSettings, setShowTextSettings] = useState(true); // State to control whether the settings panel is shown or collapsed
    const [showPositionSettings, setShowPositionSettings] = useState(true); // State to control whether the settings panel is shown or collapsed
    const [showFontSettings, setShowFontSettings] = useState(true); // State to control whether the settings panel is shown or collapsed
    const [showTextStyleSettings, setShowTextStyleSettings] = useState(true); // State to control whether the settings panel is shown or collapsed
    const [legends, setLegends] = useState([]); // To store the list of legends (name and ID)
    const [showTagManager, setShowTagManager] = useState(false); // Modal visibility
    const [newTagName, setNewTagName] = useState(
      t('text-editor-component-new-tag')
    );
    const [newTagColor, setNewTagColor] = useState(
      predefinedTagColors.find(
        (predefinedColor, index) => !tags.some((tag) => tag.id === index + 1)
      ) // Find the first color in predefinedTagColors that is not used in any tag (this is needed during initialization because tags may be imported from localStorage or an imported design)
    );
    const [selectedTagPosition, setSelectedTagPosition] = useState(null); // stores the position of tag (the position of the text to wich the text belongs to)
    const [hoveredIndex, setHoveredIndex] = useState(null); // state to save the index of the tag the user over over
    const [showTagDropdown, setShowTagDropdown] = useState(false);
    const tagDropdownRef = useRef(null); // ref to the tag dropdown menue
    const menuRef = useRef(null); // ref the the <Dropdown.Menu> element
    const [deleteTextColorMode, setDeleteTextColorMode] = useState(false); // state to toggle delete text colors mode on and off
    const [fontStyleSearchQuery, setFontStyleSearchQuery] = useState('');
    const [showFontFamilyDropdown, setShowFontFamilyDropdown] = useState(false);
    const [showFontStyleDropdown, setShowFontStyleDropdown] = useState(false); // To control style dropdown visibility
    const [allStyles, setAllStyles] = useState([]);
    const stylesCacheRef = useRef({}); // finished font style requests
    const stylePromisesRef = useRef({}); // in-flight font style requests
    const [variationsOfSelectedFont, setVariationsOfSelectedFont] = useState(
      []
    ); // variation values, based on the selected font
    const [fontPreviews, setFontPreviews] = useState([]);
    const [defaultFontFamily, setDefaultFontFamily] = useState('');
    const [defaultFontName, setDefaultFontName] = useState('');
    const [defaultFontWeight, setDefaultFontWeight] = useState(null);
    const [defaultFontStretch, setDefaultFontStretch] = useState(null);
    const [defaultFontStyle, setDefaultFontStyle] = useState(null);
    const [fontFamilySearchQuery, setFontFamilySearchQuery] = useState('');
    const sortedFontPreviews = useMemo(
      () => sortFontPreviewsBySearch(fontPreviews, fontFamilySearchQuery),
      [fontPreviews, fontFamilySearchQuery]
    ); // the font picker's list: the families the search matches first, then the rest (utils/fontHelpers.js), recomputed only when the previews or the search change
    const defaultStyle = useMemo(
      () => ({
        fontFamily: defaultFontName,
        fontSize: 6,
        fill: '#000000',
        letterSpacing: 0,
        wordSpacing: 0,
        strokeWidth: 0.5,
        stroke: '#000000',
        fontWeight: defaultFontWeight,
        fontStretch: defaultFontStretch,
        fontStyle: defaultFontStyle,
      }),
      [defaultFontName, defaultFontWeight, defaultFontStretch, defaultFontStyle]
    ); // Keep this object’s reference stable with useMemo. Without it, a new object is created every render (triggering needless work and deps warnings). It only recalculates when the listed deps change.
    const defaultTextPosition = useMemo(
      () => ({
        isoSection: isoEnterSelected ? 'top' : 'none',
        gridStructure: '3x3',
        gridRow: 2,
        gridColumn: 2,
        cellAlignment: 'center',
        boundingBox: 'standard',
        rotation: 0,
        xOffset: 0,
        yOffset: 0,
      }),
      [isoEnterSelected]
    ); // Keep this object’s reference stable with useMemo. Without it, a new object is created every render (triggering needless work and deps warnings). It only recalculates when the listed deps change.
    const defaultTextObject = useMemo(
      () => ({
        text: 'A',
        fontFamilyName: defaultFontFamily,
        style: defaultStyle,
        customStyles: {
          highlight: defaultHighlight,
          underline: defaultUnderline,
          strikethrough: defaultStrikethrough,
        },
        position: defaultTextPosition,
        isActiveStates: defaultIsActiveStates,
        selectedTextColorIndexes: defaultSelectedTextColorIndexes,
        tagId: null,
        visible: true,
      }),
      [defaultFontFamily, defaultStyle, defaultTextPosition]
    ); // Keep this object’s reference stable with useMemo. Without it, a new object is created every render (triggering needless work and deps warnings). It only recalculates when the listed deps change.

    const {
      exactlyOneSelected,
      selectedTextObject,
      selectedTagId,
      singleSelectedKeycap,
    } = useMemo(() => {
      const exactlyOneSelected = selectedKeycaps.length === 1; // check if the use has exactly one keycap selected

      let selectedTextObject = defaultTextObject;
      let selectedTagId = null;
      let singleSelectedKeycap = null;

      if (exactlyOneSelected) {
        const { rowIndex, keycapIndex } = selectedKeycaps[0];
        const kc = keycapData?.[rowIndex]?.[keycapIndex];

        if (kc && !kc.isSpacer) {
          selectedTextObject =
            kc.texts?.[selectedTextIndex] ?? defaultTextObject;
          selectedTagId = kc.texts?.[selectedTagPosition]?.tagId ?? null;
          singleSelectedKeycap = { rowIndex, keycapIndex };
        }
      }

      return {
        exactlyOneSelected,
        selectedTextObject,
        selectedTagId,
        singleSelectedKeycap,
      };
    }, [
      selectedKeycaps,
      keycapData,
      selectedTextIndex,
      selectedTagPosition,
      defaultTextObject,
    ]); // useMemo ensures that the exactlyOneSelected, selectedTextObject and selectedTagId variables will be recomputed if any of the dependencies change

    const targets = collectTargetEntries({
      keycapData,
      entriesKey: 'texts',
      selectedKeycaps,
      selectedIndex: selectedTextIndex,
      selectedTag,
    }); // the text entries the settings apply to (none for a single selected text entry)
    const isControlDisabled =
      targets.length === 0 && !(exactlyOneSelected && selectedTextIndex); // enabled controls when at least one keycap is selected and at least one of the selected keycaps have a text entry, or if the user has exactly one keycap and text entry selecred, or if the user has selected a tag which is referenced by at least one text entry

    const [tempFontWeight, setTempFontWeight] = useState(''); // this temp value allows the user to type in an invalid number, before it is validated
    const [tempFontStretch, setTempFontStretch] = useState(''); // this temp value allows the user to type in an invalid number, before it is validated
    const [tempFontStyle, setTempFontStyle] = useState(''); // this temp value allows the user to type in an invalid number, before it is validated

    function getFontStyles(fontFamily) {
      return fetchFontStyles(
        fontFamily,
        stylesCacheRef.current,
        stylePromisesRef.current
      );
    } // the styles of a font family, fetched once per editor instance (utils/fontHelpers.js, with this editor's caches)

    const didInitFontUIRef = useRef(false);

    useEffect(() => {
      if (didInitFontUIRef.current) return; // prevent double init (e.g., React 18 StrictMode)
      didInitFontUIRef.current = true;

      let cancelled = false;

      (async () => {
        // 1. Fetch font previews + set default font states:
        const { data } = await api.get('/api/fonts/previews'); // fetching the preview URLs of all fonts (png previews of how the fonts look like)
        if (cancelled) return;

        setFontPreviews(data); // Set the font previews data to the state

        const defaultFont =
          data.find((font) => font.fontFamily === 'notosans') ??
          data[0] ??
          null; // Find the font with the fontFamily of 'notosans' (if no 'notosans' font found, default to the first font in the list). If I change the default font I also need to update the default font used in the LegendsEditScreen. Also I need to check all existing predefined legends, if any of the glyphs is use there are not supported by the new default font.
        if (!defaultFont) return;

        const defaultFamily = defaultFont.fontFamily;
        const defaultFontName = defaultFont.primaryFont.fontName;

        setDefaultFontFamily(defaultFamily); // save the default font family for later access when creating new text objects
        setDefaultFontName(defaultFontName); // save the default font name for later access when creating new text objects

        const defaultStyles = await getFontStyles(defaultFamily); // load all available font styles for the default font family
        if (cancelled) return;

        const primaryFont =
          defaultStyles.find((s) => s.isPrimary) ?? defaultStyles[0] ?? null; // Find the primary font

        const weightVariation =
          primaryFont?.variations?.find((variation) => variation.tag === 'wght')
            ?.defaultValue ?? null; // Find the default weight variation
        const widthVariation =
          primaryFont?.variations?.find((variation) => variation.tag === 'wdth')
            ?.defaultValue ?? null; // Find the default width variation
        const slantVariation =
          primaryFont?.variations?.find((variation) => variation.tag === 'slnt')
            ?.defaultValue ?? null; // Find the default slant variation

        setDefaultFontWeight(weightVariation); // Set default weight or null
        setDefaultFontStretch(widthVariation); // Set default width or null
        setDefaultFontStyle(slantVariation); // Set default slant or null

        // 2. Set UI related font states
        // 2.1 If the user has selected a specific text entry, set font states based on the values of the selected text entry:
        const exactlyOne = selectedKeycaps.length === 1;
        let initializedFromSelection = false;

        if (exactlyOne && selectedTextIndex != null) {
          // if the user has selected exactly one keycap and also selected a specific text entry:
          const { rowIndex, keycapIndex } = selectedKeycaps[0];
          const text =
            keycapData?.[rowIndex]?.[keycapIndex]?.texts?.[
              String(selectedTextIndex)
            ] ?? null; // get the data of the selected text entry

          if (text) {
            const styles =
              text.fontFamilyName === defaultFamily
                ? defaultStyles
                : await getFontStyles(text.fontFamilyName); // load all available font styles for the font family of the selected text entry (or use the already loaded defaultStyles, if the font family of the selected text entry matches the default font family)
            if (cancelled) return;

            setAllStyles(styles); // set all available font styles for the font family of the selected text entry

            const font =
              styles.find((s) => s.fontName === text.style.fontFamily) ||
              styles.find((s) => s.isPrimary) ||
              styles[0] ||
              null; // find the font of the selected text entry (or default to the primary font)

            // set UI related font states based on the selected text entry:
            setFontStyleSearchQuery(font?.style ?? '');
            setVariationsOfSelectedFont(font?.variations ?? []);
            setTempFontWeight(
              text.style.fontWeight ??
                font?.variations?.find((v) => v.tag === 'wght')?.defaultValue ??
                null
            );
            setTempFontStretch(
              text.style.fontStretch ??
                font?.variations?.find((v) => v.tag === 'wdth')?.defaultValue ??
                null
            );
            setTempFontStyle(
              text.style.fontStyle ??
                font?.variations?.find((v) => v.tag === 'slnt')?.defaultValue ??
                null
            );
            setFontFamilySearchQuery(text.fontFamilyName);
            initializedFromSelection = true;
          }
        }

        // 2.2 If the UI related font states have not been initialized above:
        if (!initializedFromSelection) {
          const hasSelection = selectedKeycaps.length > 0; // check if the user has at least one keycap selected
          const hasAnyTextInSelection = selectedKeycaps.some(
            ({ rowIndex, keycapIndex }) => {
              const kc = keycapData?.[rowIndex]?.[keycapIndex];
              if (!kc || kc.isSpacer) return false;
              return Object.keys(kc.texts || {}).length > 0;
            }
          ); // check if any of the selected keycaps hold at least one text entry (false if none of the selected keycaps have any text entries)

          const tagTargetsExist =
            collectTargetEntries({
              keycapData,
              entriesKey: 'texts',
              selectedTag,
            }).length > 0; // whether at least one text entry references the selected tag (none without a tag)

          const shouldInitDefaults =
            !(hasSelection && hasAnyTextInSelection) && !tagTargetsExist; // init defaults only if: no valid keycap+text selection AND no tag-driven targets exist

          if (shouldInitDefaults) {
            setAllStyles(defaultStyles);
            setVariationsOfSelectedFont(primaryFont?.variations ?? []);
            setFontFamilySearchQuery(defaultFamily);
            setFontStyleSearchQuery(primaryFont?.style ?? '');
            setTempFontWeight(weightVariation);
            setTempFontStretch(widthVariation);
            setTempFontStyle(slantVariation);
          }
        }

        // 2.3 The case, when the user has no specific text entry selected, but has selected at least one keycap with at least one text entry inside it or the user has selected a tag that is referenced by at least one text entry, is handled in a separate useEffect that depends on commonTextSettings. This handles the case when all text entries of all selected keycaps or all text entries that reference the selected tag are being edited.
      })();

      return () => {
        // Cleanup: prevent late async setState
        cancelled = true;
      };
    }, []); // useEffect hook to fetch the preview URLs of all fonts, the fonts of the default (or selected) font family and set/initialize font related states

    useEffect(() => {
      const fetchLegends = async () => {
        try {
          const { data } = await api.get('/api/legends');
          setLegends(data); // Set the list of legend names and IDs
        } catch (err) {
          console.error('Error fetching legends:', err.message);
        }
      };

      fetchLegends();
    }, []); // Fetch all legends (name + id) when the component mounts

    useEffect(() => {
      if (selectedTextIndex == null) return;

      const unselectOnEscape = (e) => {
        if (e.key === 'Escape') {
          updateDesign({ 'textEditor.selectedTextIndex': null });
        }
      };

      window.addEventListener('keydown', unselectOnEscape);
      return () => window.removeEventListener('keydown', unselectOnEscape);
    }, [selectedTextIndex, updateDesign]); // Escape unselects the text entry, the keyboard equivalent of clicking next to the entries

    useEffect(() => {
      const mediaQueryXL = window.matchMedia('(max-width: 1199px)'); // Media query for XL screens (≤1199px)
      const mediaQueryMD = window.matchMedia('(max-width: 767px)'); // Media query for MD screens (≤767px)

      const handleResize = () => {
        // Update XL screen state:
        const isXL = mediaQueryXL.matches;
        setScreenWidthXL(isXL);

        // Update MD screen state:
        const isMD = mediaQueryMD.matches;
        setScreenWidthMD(isMD);
      }; // Handler function that runs when the screen size crosses the 1199px and 767px thresholds

      handleResize(); // Set initial state on mount

      // Attach listeners to both media queries:
      mediaQueryXL.addEventListener('change', handleResize);
      mediaQueryMD.addEventListener('change', handleResize);

      return () => {
        // Cleanup event listeners on unmount:
        mediaQueryXL.removeEventListener('change', handleResize);
        mediaQueryMD.removeEventListener('change', handleResize);
      };
    }, []); // this useEffect updates the screenWidthXL and screenWidthMD states based on the screen size

    useEffect(() => {
      if (!showTagDropdown) return;

      const onDocClick = (e) => {
        if (!tagDropdownRef.current) return;
        if (!tagDropdownRef.current.contains(e.target))
          setShowTagDropdown(false);
      };

      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [showTagDropdown]); // if the tag dropdown is opened and the user clicks outside, the dropdown closes

    useEffect(() => {
      if (!showTagDropdown) return;

      const el = menuRef.current; // get the DOM element of the dropdown menu
      if (!el) return;

      const update = () => {
        const hasScrollbar = el.scrollHeight > el.clientHeight; // true if content height is bigger than container height
        el.setAttribute('data-scrollable', hasScrollbar ? 'true' : 'false'); // mark menu as scrollable or not (so that I can adjust the css in index.css based on if the menu shows a scroll bar)
      }; // function to check if a vertical scrollbar (inside the Dropdown.Menu) exists

      update(); // run initial measurement

      // keep measurements updated whenever the element's size/content changes:
      const ro = new ResizeObserver(update);
      ro.observe(el);

      window.addEventListener('resize', update); // also recheck measurements when the window resizes

      return () => {
        // cleanup on effect unmount or dependency change:
        ro.disconnect(); // stop observing size/content changes
        window.removeEventListener('resize', update); // remove resize listener
      };
    }, [showTagDropdown]); // runs when the dropdown menu is shown/hidden to measure whether it has a vertical scrollbar and update a custom 'data-scrollable' attribute variable accordingly (this variable is than used inside index.css to change the position of the menu, depending on if a scroll bar is added)

    const applyLegends = (keycapData, legends, filters) =>
      assignLegendsToLayout(keycapData, legends, filters, {
        defaultTextObject,
        defaultIconObject: defaultLegendIconObject,
      }); // the legends over the design's keycaps, each over the editor's default entry (utils/legendAssignment.js); the language select and the three setting controls apply them

    const handleLanguageChange = (id) => {
      if (id) {
        // if a language is selected (id is not an empty string), fetch the language from the database
        const fetchLegend = async () => {
          try {
            const { data } = await api.get(`/api/legends/${id}`);

            setSelectedLegend(data); // set the full legend document

            updateDesign({
              'textEditor.predefinedLegends.id': id,
              keycapData: (prevKeycapData) =>
                applyLegends(prevKeycapData, data.legends, {
                  configurationStyle,
                  os,
                  legendStyle,
                }), // assign legends to the design layout
            });
          } catch (err) {
            console.error('Error fetching legend:', err.message);
          }
        };

        fetchLegend();
      } else {
        // if no language is selected, just update the legend ID in the design state
        updateDesign({
          'textEditor.predefinedLegends.id': id,
        });
      }
    }; // fetch the full legend document when the user selects a language and assign the legends to the design (I use this function instead of a useEffect hook, to ensure that legends are only assigned to the design whenever the user selects a language. Otherwise a useEffect hook would assign the legends whenever the 'language' state changes, which also was the case when the user already had a value for 'language' in the localStorage set and it would be set as the state whenever the user visits the site. This caused all legends to be overwritten by the selected predefiend legends)

    const createTag = (name, color) => {
      const trimmedName = name.trim(); // remove leading/trailing spaces

      if (!trimmedName) {
        toast(t('text-editor-component-name-empty'));
        return;
      } // prevent adding a new tag if the name is empty after trimming

      updateDesign({
        'textEditor.tags': (prevTags) => {
          if (prevTags.length >= 20) {
            toast(t('text-editor-component-maximum-of-20-tags'));
            return prevTags;
          } // Prevent adding a new tag if the maximum of 20 tags has been reached

          if (trimmedName.length > 100) {
            toast(t('text-editor-component-name-too-long'));
            return prevTags;
          } // prevent adding a new tag if the name exceeds 100 characters

          if (!color || !predefinedTagColors.includes(color)) {
            toast(t('text-editor-component-invalid-or-missing-color'));
            return prevTags;
          } // ensure the color is valid and present in predefinedTagColors. Reachable, although the palette disables the colours in use: undo and redo (Ctrl+Z, Ctrl+Y, which fire under the open tag manager too) change the tags beneath the chosen colour, so no colour (after the twentieth tag was undone) or a taken one can arrive here (checked Oct 2026)

          const colorIndex = predefinedTagColors.indexOf(color) + 1; // Determine the color index based on predefinedTagColors (Starting from 1, because 0 is treated as falsy value and using it as an ID can lead to unexpected behavior in conditional checks)

          if (prevTags.some((tag) => tag.id === colorIndex)) {
            toast(t('text-editor-component-color-already-assigned'));
            return prevTags;
          } // Prevent adding a new tag with a duplicate color (see the guard above for how one arrives)

          if (
            prevTags.some(
              (tag) => tag.name.toLowerCase() === trimmedName.toLowerCase()
            )
          ) {
            toast(t('text-editor-component-name-already-in-use'));
            return prevTags;
          } // Prevent adding a new tag with a duplicate name

          const newTag = {
            id: colorIndex, // Unique ID for the tag (1-20)
            name: trimmedName,
          };

          const updatedTags = [...prevTags, newTag]; // adding the new tag

          const firstAvailableColor = predefinedTagColors.find(
            (predefinedColor, index) =>
              !updatedTags.some((tag) => tag.id === index + 1)
          ); // Find the first color in predefinedTagColors that is not used in any tag

          setNewTagColor(firstAvailableColor || null); // Set the new tag color to the first available color after creating the tag

          return updatedTags;
        },
      });
    }; // function to create new tags

    const deleteTag = (index) => {
      if (index < 0 || index >= tags.length) return; // invalid index

      const tag = tags[index];
      const tagId = tag?.id ?? null; // id of the tag we’re deleting

      let freedColor = null; // will hold the color freed by the deleted tag
      if (tagId != null) {
        const colorIdx = tagId - 1;
        if (colorIdx >= 0 && colorIdx < predefinedTagColors.length) {
          freedColor = predefinedTagColors[colorIdx]; // remember the freed color so we can set it after state update if needed
        }
      }

      updateDesign((prev) => {
        const tags = prev.textEditor?.tags || [];
        const nextTags = tags.filter((_, i) => i !== index); // remove tag from list

        const nextKeycapData = prev.keycapData.map((row) =>
          row.map((kc) => {
            // go through all keycaps:
            if (!kc || kc.isSpacer || !kc.texts) return kc; // skip invalid, spacer and non-text keycaps

            const texts = kc.texts;
            let changed = false;

            const updated = {};
            for (const [k, v] of Object.entries(texts)) {
              // go through all text entries of the current keycap:
              if (v && v.tagId === tagId) {
                changed = true;
                updated[k] = { ...v, tagId: null }; // remove tag reference from the current text entry, if it matches the deleted tag
              } else {
                updated[k] = v;
              }
            }

            return changed ? { ...kc, texts: updated } : kc;
          })
        );

        return {
          ...prev,
          textEditor: {
            ...prev.textEditor,
            tags: nextTags, // update tags list
          },
          keycapData: nextKeycapData, // update tag refs in texts
        };
      });

      if (newTagColor == null && freedColor) {
        setNewTagColor(freedColor); // if no color is selected for the "new tag" picker, adopt the freed color
      }
    }; // deletes a tag (by index) and removes its id from all text entries

    const handleSelectCell = (gridRow, gridColumn) => {
      handleKeycapChange(
        'texts',
        {
          'position.gridRow': gridRow,
          'position.gridColumn': gridColumn,
        },
        selectedTextIndex
      );
    }; // updates selected row and column to the selected text object

    const handleSelectCellAlignment = (alignment) => {
      handleKeycapChange(
        'texts',
        { 'position.cellAlignment': alignment },
        selectedTextIndex
      );
    }; // updates selected cellAlignment to the selected text object

    const handleKeycapChange = async (
      property, // The property of the keycap to be updated (e.g., 'texts')
      updates, // Object containing sub-property paths and their new values
      textPosition = null, // Optional parameter specifying the position of a text object within the `texts` object
      addNew = false, // Flag indicating if a new text object should be added
      deleteTextKey = null // Optional parameter for deleting a specific text entry
    ) => {
      updateDesign((prevDesign) => {
        const newDesign = { ...prevDesign }; // Clone the previous design state
        const selectedKeycaps = prevDesign.settings?.selectedKeycaps || [];
        const exactlyOneSelected = selectedKeycaps.length === 1; // check if the user has exactly one keycap selected

        const selectedTag = prevDesign.textEditor?.selectedTag ?? null; // get the selected tag
        const tagMode = selectedTag != null;

        const selectedSet = new Set(
          selectedKeycaps.map(
            ({ rowIndex, keycapIndex }) => `${rowIndex}:${keycapIndex}`
          )
        ); // create a fast lookup for all selected keycaps ("row:keycap")

        if (
          property === 'texts' &&
          updates?.text &&
          updates.text.length > 100
        ) {
          toast(t('text-editor-component-maximum-of-100-characters'));
          return prevDesign; // if the new text content exceeds 100 characters, quit
        }

        if (tagMode && (addNew || deleteTextKey != null)) {
          return prevDesign; // adding/deleting a specific text entry does not make sense in tag mode
        }

        const hasIsoSectionUpdate =
          property === 'texts' &&
          Object.prototype.hasOwnProperty.call(
            updates || {},
            'position.isoSection'
          ); // detect if this update includes updating the 'position.isoSection' field of the text entries

        const updateTextsOfKeycap = (
          keycap,
          perKeycapUpdates,
          shouldUpdateEntry
        ) => {
          if (!perKeycapUpdates || Object.keys(perKeycapUpdates).length === 0)
            return keycap; // nothing to apply

          const currentTexts = keycap.texts || {};
          const nextTexts = Object.entries(currentTexts).reduce(
            (acc, [pos, textObj]) => {
              if (textObj && shouldUpdateEntry(pos, textObj)) {
                // if the current text entry should be updated (shouldUpdateEntry):
                acc[pos] = updateNestedField(textObj || {}, perKeycapUpdates); // update the current text entry
              } else {
                acc[pos] = textObj; // otherwise return the text entry as it is
              }
              return acc;
            },
            {}
          );
          return { ...keycap, texts: nextTexts };
        }; // helper: update all (or filtered) text entries of a keycap

        const updateSingleTextEntryOfKeycap = (
          keycap,
          pos,
          perKeycapUpdates
        ) => {
          if (!perKeycapUpdates || Object.keys(perKeycapUpdates).length === 0)
            return keycap; // nothing to apply

          const currentTexts = keycap.texts || {};
          const updatedTextObject = updateNestedField(
            currentTexts[pos] || {},
            perKeycapUpdates
          );

          return {
            ...keycap,
            texts: { ...currentTexts, [pos]: updatedTextObject }, // Set the updated text entry back into the texts object
          };
        }; // Helper: apply "updates" to a specific text position of a single keycap

        const singleSel = exactlyOneSelected ? selectedKeycaps[0] : null; // only allow adding/deleting text entries, if exactly one keycap selected

        newDesign.keycapData = prevDesign.keycapData.map((row, rIndex) => {
          return row.map((keycap, kIndex) => {
            // go through all keycaps inside keycapData:
            const isSelected = selectedSet.has(`${rIndex}:${kIndex}`); // check if the current keycap is selected
            const isActual = keycap && !keycap.isSpacer; // make sure the current keycap is not empty and not a spacer
            if (!isActual) return keycap; // skip invalid keycaps

            // 1. ADD NEW TEXT ENTRY:
            if (property === 'texts' && addNew && !tagMode) {
              if (
                !exactlyOneSelected ||
                rIndex !== singleSel.rowIndex ||
                kIndex !== singleSel.keycapIndex
              ) {
                // adding text entries is only allowed, if the user has exactly one keycap selected
                // skip the current keycap, if it is not the selected keycap
                return keycap;
              }

              const currentTexts = keycap.texts || {};
              const count = Object.keys(currentTexts).length;
              if (count >= 10) {
                toast(t('text-editor-component-maximum-of-10-texts'));
                return keycap; // make sure the user can't add more than 10 text entries to the keycap
              }

              const nextKey = String(count + 1);
              const nextKeycap = {
                ...keycap,
                texts: {
                  ...currentTexts,
                  [nextKey]: deepClone(defaultTextObject), // add the default text entry
                },
              };

              setDeleteTextColorMode(false); // exit delete text color mode

              handleSelectFontFamily(
                sortedFontPreviews.find(
                  (font) => font.fontFamily === defaultFontFamily
                ),
                false, // don't apply updates to the new text object inside the handleSelectFontFamily, as the text object has not yet been added to the keycap when this function is being called
                true
              ); // preparing the text editor for a newly added text object, by fetching all fonts of the default font family and setting states based on the default font (primary font of the default font family)

              newDesign.textEditor = {
                ...prevDesign.textEditor,
                selectedTextIndex: nextKey, // update selectedTextIndex in newDesign
              };
              return nextKeycap;
            }

            // 2. DELETE A TEXT ENTRY:
            if (property === 'texts' && deleteTextKey != null && !tagMode) {
              if (
                !exactlyOneSelected ||
                rIndex !== singleSel.rowIndex ||
                kIndex !== singleSel.keycapIndex
              ) {
                // adding text entries is only allowed, if the user has exactly one keycap selected
                // skip the current keycap, if it is not the selected keycap
                return keycap;
              }

              // remove the text entry and reindex the remaining:
              const currentTexts = keycap.texts || {};
              const updatedTexts = Object.entries(currentTexts)
                .filter(([key]) => key !== String(deleteTextKey)) // filter out all text entries that don't match the deleteTextKey
                .map(([, value]) => value) // get an array of remaining text objects
                .reduce((acc, text, index) => {
                  acc[String(index + 1)] = text; // re-index the remaining texts starting from 1
                  return acc;
                }, {});

              // maintain selectedTextIndex in newDesign:
              const prevSel = prevDesign.textEditor?.selectedTextIndex; // get the previous selectedTextIndex
              if (prevSel) {
                const selectedIndex = parseInt(prevSel, 10);
                const deleteIndex = parseInt(deleteTextKey, 10);

                if (deleteIndex === selectedIndex) {
                  newDesign.textEditor = {
                    ...prevDesign.textEditor,
                    selectedTextIndex: null, // if the deleted text is the current selectedTextIndex, the selectedTextIndex is set to null
                  };
                } else if (deleteIndex < selectedIndex) {
                  // if the deleted text is before the current selectedTextIndex, decrement the selectedTextIndex:
                  newDesign.textEditor = {
                    ...prevDesign.textEditor,
                    selectedTextIndex: String(selectedIndex - 1), // keep it a string value, as all conditional checks of the selectedTextIndex are done to a index of an object (which is also a string value)
                  };
                }
              }

              return { ...keycap, texts: updatedTexts };
            }

            // 3. APPLY UPDATES:
            if (property === 'texts') {
              let perKeycapUpdates = updates;

              if (hasIsoSectionUpdate && !keycap.isIsoEnter) {
                // if the keycap update(s) includes updating the 'position.isoSection' setting and the current keycap is NOT an ISO Enter keycap:
                const { ['position.isoSection']: _omit, ...rest } =
                  updates || {}; // remove the 'position.isoSection' update for the current keycap (but keep other updates/fields)
                perKeycapUpdates = rest;
              }

              const hasAnyUpdate =
                perKeycapUpdates && Object.keys(perKeycapUpdates).length > 0;
              if (!hasAnyUpdate) return keycap; // if after removing the 'position.isoSection' update nothing remains, skip the current keycap

              // 3.1 Tag selected (Update every text entry that references the selected tag):
              if (tagMode) {
                const pred = (_pos, txt) => {
                  return !!txt && txt.tagId === selectedTag;
                }; // function that checks if a text entry should be updated (when its tagId matches the selected tag)
                return updateTextsOfKeycap(keycap, perKeycapUpdates, pred); // apply the updates to all text that reference the selected tag
              }

              // 3.2 Multiple keycaps selected (no need to check for a textPosition here, because when the user has selected multiple keycaps, no text entries will be displayed and can therefor also not be selected):
              if (selectedKeycaps.length > 1) {
                if (!isSelected) return keycap;
                const pred = () => true; // all entries of this keycap
                return updateTextsOfKeycap(keycap, perKeycapUpdates, pred); // apply to all text entries of all selected keycaps
              }

              // 3.3 Single keycap selected:
              if (exactlyOneSelected && isSelected) {
                // 3.3.1 No specific text entry selected
                if (textPosition == null) {
                  const pred = () => true;
                  return updateTextsOfKeycap(keycap, perKeycapUpdates, pred); // update all text entries of the single keycap
                }

                // 3.3.2 Specific text entry selected
                return updateSingleTextEntryOfKeycap(
                  keycap,
                  textPosition,
                  perKeycapUpdates
                ); // update only that one
              }

              return keycap; // no keycaps selected -> no text updates
            }

            return keycap;
          });
        });

        return newDesign; // return the fully updated design state
      });
    }; // this function allows to dynamically add or delete a text entry within selected keycaps, or update a selected text entry (or all text entries of all selected keycaps)

    const updateTextEditor = (position) => {
      updateDesign({ 'textEditor.selectedTextIndex': position });

      const clickedTextObject =
        keycapData?.[singleSelectedKeycap?.rowIndex]?.[
          singleSelectedKeycap?.keycapIndex
        ]?.texts?.[position] ?? null; // accessing the text object the user clicked on

      setDeleteTextColorMode(false); // Disable delete text color mode

      const fontFamilyName = clickedTextObject.fontFamilyName;
      const fontFamily = clickedTextObject.style.fontFamily;

      getFontStyles(fontFamilyName).then((data) => {
        setAllStyles(data); // Set all available styles for the font (holds all fonts of the font family from the selected text object)

        const font = data.find(
          (fontStyle) => fontStyle.fontName === fontFamily
        ); // get the font of the selected text object

        setFontStyleSearchQuery(font.style);
        setVariationsOfSelectedFont(font.variations); // Store the variations of the font (the font of the selected text object)

        const weightVariation = font.variations.find(
          (variation) => variation.tag === 'wght'
        ); // Get weight variation
        const widthVariation = font.variations.find(
          (variation) => variation.tag === 'wdth'
        ); // Get width variation
        const slantVariation = font.variations.find(
          (variation) => variation.tag === 'slnt'
        ); // Get slant variation

        setTempFontWeight(
          clickedTextObject.style.fontWeight !== null
            ? clickedTextObject.style.fontWeight // Use fontWeight from clickedTextObject if defined
            : (weightVariation?.defaultValue ?? null) // Otherwise use default weight or null
        ); // Set temp font weight

        setTempFontStretch(
          clickedTextObject.style.fontStretch !== null
            ? clickedTextObject.style.fontStretch // Use fontStretch from clickedTextObject if defined
            : (widthVariation?.defaultValue ?? null) // Otherwise use default stretch or null
        ); // Set temp font stretch

        setTempFontStyle(
          clickedTextObject.style.fontStyle !== null
            ? clickedTextObject.style.fontStyle // Use fontStyle from clickedTextObject if defined
            : (slantVariation?.defaultValue ?? null) // Otherwise use default slant or null
        ); // Set temp font style
      });

      setFontFamilySearchQuery(fontFamilyName);
      setShowFontFamilyDropdown(false); // Hide the dropdown after selecting a font
    }; // this function updates states in the text editor, which are not found in the text object itself

    useImperativeHandle(ref, () => ({
      handleKeycapChange,
      updateTextEditor,
    })); // Expose the handleKeycapChange and updateTextEditor functions to the parent (CustomizerScreen.js)

    const handleAddTextColor = (currentColor, setting, colorPath) => {
      updateDesign((prev) => {
        const prevColors = prev.textEditor?.colors ?? [];
        const newColors = [...prevColors, currentColor]; // Add new color
        const newIndex = newColors.length - 1; // Get index of the newly added color
        const hasSelection = (prev.settings?.selectedKeycaps || []).length > 0;

        if (!isControlDisabled) {
          // only try updating text entries when there are any target text entries:
          handleKeycapChange(
            'texts',
            {
              [`selectedTextColorIndexes.${setting}`]: newIndex, // automatically reference the new color for the specified setting
              [colorPath]: currentColor, // apply the color to the setting
            },
            hasSelection ? (prev.textEditor?.selectedTextIndex ?? null) : null // in tagMode we pass null (in tag mode there is no selected text entry)
          );
        }

        return {
          ...prev,
          textEditor: {
            ...prev.textEditor,
            colors: newColors,
          },
        };
      });
    }; // adding a new color to the textColors array

    const toggleDeleteTextColorMode = () => {
      setDeleteTextColorMode(!deleteTextColorMode);
    }; // toggling the delete text color mode state

    const handleSelectFontFamily = (
      font,
      shouldUpdateKeycap = true,
      shouldUpdateUI = false
    ) => {
      setFontFamilySearchQuery(font.fontFamily); // update the font family in the UI
      setShowFontFamilyDropdown(false); // hide the dropdown after selecting a font

      // load styles (fonts) of the chosen family:
      getFontStyles(font.fontFamily).then((styles) => {
        setAllStyles(styles); // save all available styles for the font (holds all fonts of the selected font family)

        const primaryFont =
          styles.find((s) => s.isPrimary) ?? styles[0] ?? null; // get the primary font from the selected font family

        const defaultWght =
          primaryFont?.variations?.find((v) => v.tag === 'wght')
            ?.defaultValue ?? null; // get the default weight variation
        const defaultWdth =
          primaryFont?.variations?.find((v) => v.tag === 'wdth')
            ?.defaultValue ?? null; // get the default width variation
        const defaultSlnt =
          primaryFont?.variations?.find((v) => v.tag === 'slnt')
            ?.defaultValue ?? null; // get the default slant variation

        // update the UI related font states:
        const selectedKeycaps = design.settings?.selectedKeycaps || [];
        const exactlyOne = selectedKeycaps.length === 1;
        const selectedTextIndex = design.textEditor?.selectedTextIndex ?? null;
        const hasSpecific = selectedTextIndex != null;

        if ((exactlyOne && hasSpecific) || shouldUpdateUI) {
          // if the user has exactly one keycap and a specific text entry selected -> we *can* set concrete font UI states, because that one entry is authoritative:
          setFontStyleSearchQuery(primaryFont?.style ?? ''); // update the UI to the chosen family's primary font
          setVariationsOfSelectedFont(primaryFont?.variations ?? []); // save the variations of the primary font
          setTempFontWeight(defaultWght); // update the UI to the default font weight
          setTempFontStretch(defaultWdth); // update the UI to the default font stretch
          setTempFontStyle(defaultSlnt); // update the UI to the default font slant
        } else {
          // If the user has selected one or more keycaps, but not a specific text entry -> do NOT overwrite UI related font states.
          // The useEffect that depends on commonTextSettings will refresh these UI fields to common or "mixed".
        }

        if (!shouldUpdateKeycap) {
          return; // don't apply the updates to the selected keycap, if this function has been in the process of adding a new text object to a keycap (this is because the text object has not yet been added to the keycap at the time that this function runs)
        }

        // update the selected text entries:
        updateDesign((prev) => {
          const selectedKeycaps = prev.settings?.selectedKeycaps || [];
          const selectedTextIndex = prev.textEditor?.selectedTextIndex ?? null;

          const exactlyOne = selectedKeycaps.length === 1;
          const hasSpecific = selectedTextIndex != null;

          const selectedTag = prev.textEditor?.selectedTag ?? null;
          const tagMode = selectedTag != null;

          const selectedSet = new Set(
            selectedKeycaps.map(
              ({ rowIndex, keycapIndex }) => `${rowIndex}:${keycapIndex}`
            )
          ); // build a quick lookup for selected keycaps

          const maybeUpdateText = (textObj) => {
            if (!textObj) return textObj;
            if (textObj.fontFamilyName === font.fontFamily) {
              return textObj; // don't update this text entry, if this it already has the new font family applied
            }

            // if this text entry does not use this font family -> update all font related values inside that text entry:
            return {
              ...textObj,
              fontFamilyName: font.fontFamily, // update font family
              style: {
                ...textObj.style,
                fontFamily:
                  primaryFont?.fontName ?? textObj.style?.fontFamily ?? '', // update the font to the primary font of the new font family
                fontWeight: defaultWght, // apply default font weight
                fontStretch: defaultWdth, // apply default font stretch
                fontStyle: defaultSlnt, // apply default font slant
              },
            };
          }; // helper: update a single text entry ONLY if it doesn't already have this font family applied

          const nextKeycapData = prev.keycapData.map((row, rIndex) =>
            row.map((keycap, kIndex) => {
              // go through all keycaps:
              if (!keycap || keycap.isSpacer) return keycap; // skip spacers/invalid

              const texts = keycap.texts || {};

              if (tagMode) {
                // if the user has selected a tag -> update all text entries that reference this tag:
                let changed = false;
                const nextTexts = Object.fromEntries(
                  Object.entries(texts).map(([tKey, tObj]) => {
                    // go through all text entries of the current keycap:
                    if (tObj && tObj.tagId === selectedTag) {
                      // if the tagId of the current text entry matches the selected tag:
                      const updated = maybeUpdateText(tObj); // update the current text entry (if it doesn't already have the new font family applied)
                      if (updated !== tObj) changed = true;
                      return [tKey, updated];
                    }

                    return [tKey, tObj];
                  })
                );

                return changed ? { ...keycap, texts: nextTexts } : keycap; // only update the texts of the current keycap, if any text entries inside it changed
              }

              if (!selectedSet.has(`${rIndex}:${kIndex}`)) return keycap; // skip non-selected keycaps

              if (exactlyOne && hasSpecific) {
                // if the user has exactly one keycap and a specific text entry selected -> update only that one text entry:
                const key = String(selectedTextIndex);
                if (!texts[key]) return keycap;

                const updated = maybeUpdateText(texts[key]); // update the text entry
                if (updated === texts[key]) return keycap; // if the text entry did not change, skip updating the texts of the current keycap

                return {
                  ...keycap,
                  texts: { ...texts, [key]: updated },
                };
              }

              // if the user has not selected a specific text entry -> update all text entries of the current keycap (but only the ones that don't already have this font family applied):
              let changed = false;
              const nextTexts = Object.fromEntries(
                Object.entries(texts).map(([tKey, tObj]) => {
                  // go through all text entries of the current keycap:
                  const updated = maybeUpdateText(tObj); // update the current text entry (if it doesn't already have this font family applied)
                  if (updated !== tObj) changed = true;
                  return [tKey, updated];
                })
              );

              return changed ? { ...keycap, texts: nextTexts } : keycap;
            })
          );

          return { ...prev, keycapData: nextKeycapData };
        });
      });
    }; // this function fetches all fonts of a selected font family and sets states based on the primary font of the font family, optionally applying all updates to the keycap

    const handleSelectFontStyle = (style) => {
      setFontStyleSearchQuery(style.style || ''); // update the UI to the selected font style
      setVariationsOfSelectedFont(style.variations || []); // save the variations of the selected font
      setShowFontStyleDropdown(false); // hide the dropdown after selecting a style

      // extract default weight, width, and slant of the selected font (if they exist):
      const defWght =
        style.variations?.find((v) => v.tag === 'wght')?.defaultValue ?? null;
      const defWdth =
        style.variations?.find((v) => v.tag === 'wdth')?.defaultValue ?? null;
      const defSlnt =
        style.variations?.find((v) => v.tag === 'slnt')?.defaultValue ?? null;

      // apply to targeted texts (by tag OR by selected keycaps/text):
      updateDesign((prev) => {
        const selectedKeycaps = prev.settings?.selectedKeycaps || [];
        const selectedTextIndex = prev.textEditor?.selectedTextIndex ?? null;

        const exactlyOne = selectedKeycaps.length === 1;
        const hasSpecific = selectedTextIndex != null;

        const selectedTag = prev.textEditor?.selectedTag ?? null;
        const tagMode = selectedTag != null;

        const selectedSet = new Set(
          selectedKeycaps.map(
            ({ rowIndex, keycapIndex }) => `${rowIndex}:${keycapIndex}`
          )
        ); // build a quick lookup for selected keycaps

        const nextKeycapData = prev.keycapData.map((row, rIndex) =>
          row.map((keycap, kIndex) => {
            // go through all keycaps:
            if (!keycap || keycap.isSpacer) return keycap; // skip invalid and spacer keycaps

            const texts = keycap.texts || {};

            const maybeApplyStyle = (t) => {
              if (!t) return t;
              if (t.style?.fontFamily === style.fontName) return t; // skip updating this text entry, if it already uses this font style

              return {
                ...t,
                style: {
                  ...t.style,
                  fontFamily: style.fontName, // update the font style
                  fontWeight: defWght, // apply default font weight
                  fontStretch: defWdth, // apply default font stretch
                  fontStyle: defSlnt, // apply default font slant
                },
              };
            }; // helper: apply the new font style to a text entry if it doesn't already have it

            if (tagMode) {
              // if the user has a tag selected -> apply the changes to all text entries that reference the selected tag:
              let changed = false;

              const nextTexts = Object.fromEntries(
                Object.entries(texts).map(([tKey, tObj]) => {
                  // go through all text entries:
                  if (tObj && tObj.tagId === selectedTag) {
                    // if the current text entry references the selected tag:
                    const updated = maybeApplyStyle(tObj); // apply the changes to the current text entry
                    if (updated !== tObj) changed = true;
                    return [tKey, updated];
                  }

                  return [tKey, tObj];
                })
              );

              return changed ? { ...keycap, texts: nextTexts } : keycap;
            }

            if (!selectedSet.has(`${rIndex}:${kIndex}`)) return keycap; // if the current keycap is not selected, don't apply any changes

            if (exactlyOne && hasSpecific) {
              // if the user has exactly one keycap and a specific text entry selected -> update only that one text entry:
              const key = String(selectedTextIndex);
              const t = texts[key];
              if (!t) return keycap;

              const updated = maybeApplyStyle(t); // apply the changes to only the selected text entry
              if (updated === t) return keycap;

              return { ...keycap, texts: { ...texts, [key]: updated } };
            }

            // if the user has not selected a specific text entry -> update all text entries of the current keycap (but only the ones that don't already have this font style applied):
            let changed = false;
            const nextTexts = Object.fromEntries(
              Object.entries(texts).map(([tKey, tObj]) => {
                // go through all text entries:
                const updated = maybeApplyStyle(tObj);
                if (updated !== tObj) changed = true;
                return [tKey, updated];
              })
            );

            return changed ? { ...keycap, texts: nextTexts } : keycap;
          })
        );

        return { ...prev, keycapData: nextKeycapData };
      });

      // update the UI related font states (but not the fontFamilySearchQuery, as it stays the same when selecting a new font style):
      const exactlyOne = (design.settings?.selectedKeycaps || []).length === 1;
      const hasSpecific = design.textEditor?.selectedTextIndex != null;

      if (exactlyOne && hasSpecific) {
        // if the user has exactly one keycap and a specific text entry selected -> we *can* set concrete font UI states, because that one entry is authoritative:
        setTempFontWeight(defWght); // update the UI to the default font weight
        setTempFontStretch(defWdth); // update the UI to the default font stretch
        setTempFontStyle(defSlnt); // update the UI to the default font slant
      } else {
        // If the user has selected one or more keycaps, but not a specific text entry -> do NOT overwrite UI related font states.
        // The useEffect that depends on commonTextSettings will refresh these UI fields to common or "mixed".
      }
    }; // this function applies the selected font and it's variations to the selected text entries or all text entries that reference the selected tag. It also updates the UI related font states.

    const commonTextSettings = useMemo(
      () =>
        computeCommonSettings({
          keycapData,
          entriesKey: 'texts',
          selectedKeycaps,
          selectedIndex: selectedTextIndex,
          selectedTag,
          fieldPaths: TEXT_FIELD_PATHS,
          defaultObject: defaultTextObject,
        }),
      [
        keycapData,
        selectedKeycaps,
        selectedTextIndex,
        defaultTextObject,
        selectedTag,
      ]
    ); // the common value or MIXED of every setting across the target text entries; null while a single text entry, or none, is being edited (see utils/multiSelection.js)

    const isCommon = (value) => value != null && value !== MIXED; // small helper function to check if a value is common

    useEffect(() => {
      if (!commonTextSettings) return; // only run the following code when the text settings apply to potentially multiple text entries

      const familyName = getByPath(
        commonTextSettings,
        'fontFamilyName',
        undefined
      ); // get the value of fontFamilyName from the commonTextSettings

      if (isCommon(familyName)) {
        // if the fontFamilyName is common across all target text entries:
        setFontFamilySearchQuery(familyName); // set the selected font family in the UI to the common value

        // get all font styles of the font family:
        getFontStyles(familyName).then((styles) => {
          setAllStyles(styles); // set all available styles for the font (holds all fonts of the font family)

          const fontName = getByPath(
            commonTextSettings,
            'style.fontFamily',
            undefined
          ); // get the value of style.fontFamily (font style) from the commonTextSettings

          if (isCommon(fontName)) {
            // if the style.fontFamily (and fontFamilyName) are common across all target text entries:
            const font = styles.find((s) => s.fontName === fontName) ?? null; // get the font for the common font style

            setFontStyleSearchQuery(font?.style ?? ''); // set the selected font style in the UI to the common value
            setVariationsOfSelectedFont(font?.variations ?? []); // store the variations of the font

            // get the values of fontWeight, fontStretch and fontStyle from the commonTextSettings:
            const commonWght = getByPath(
              commonTextSettings,
              'style.fontWeight',
              undefined
            );
            const commonWdth = getByPath(
              commonTextSettings,
              'style.fontStretch',
              undefined
            );
            const commonSlnt = getByPath(
              commonTextSettings,
              'style.fontStyle',
              undefined
            );

            // if common across all the selected text entries, set the fontWeight, fontStretch and fontStyle states in the UI to the common value (otherwise empty string):
            setTempFontWeight(isCommon(commonWght) ? commonWght : '');
            setTempFontStretch(isCommon(commonWdth) ? commonWdth : '');
            setTempFontStyle(isCommon(commonSlnt) ? commonSlnt : '');
          } else {
            // if the style.fontFamily is not common across all target text entries (but the fontFamilyName is):
            setFontStyleSearchQuery('');
            setVariationsOfSelectedFont([]);
            setTempFontWeight('');
            setTempFontStretch('');
            setTempFontStyle('');
          }
        });
      } else {
        // if the fontFamilyName is not common across all target text entries → clear everything UI-side:
        setFontFamilySearchQuery('');
        setAllStyles([]);
        setFontStyleSearchQuery('');
        setVariationsOfSelectedFont([]);
        setTempFontWeight('');
        setTempFontStretch('');
        setTempFontStyle('');
      }
    }, [commonTextSettings]); // this useEffect updates UI related font states based on the commonTextSettings (when the settings apply to potentially multiple text entries at once)

    function resolveSetting(options) {
      return resolveSettingValue({
        commonSettings: commonTextSettings,
        selectedObject: selectedTextObject,
        ...options,
      });
    } // the value a control shows for a setting ({ path, defaultValue, mixedReturn }): the common value across the target text entries, or the selected text entry's

    const cellIsActive = (row, col) => {
      if (commonTextSettings) {
        // if commonTextSettings is true (not null), it means that the the user has selected one or more keycaps that hold at least one text entry, but has not selected a specific text entry (so the settings will be applied to all text entries of all selected keycaps)
        const r = commonTextSettings?.position?.gridRow;
        const c = commonTextSettings?.position?.gridColumn;

        if (r === MIXED || c === MIXED) return false; // if the text entries have mixed values for the gridRow or gridColumn → no active cell

        return (
          r === row &&
          c === col &&
          !isMixed(commonTextSettings, 'position.gridStructure')
        ); // if the text entries share the common value for the gridRow, gridColumn and gridStructure → active cell
      }

      // if no keycap is selected, or the user has a specific text entry selected → use selectedTextObject:
      return (
        selectedTextObject?.position?.gridRow === row &&
        selectedTextObject?.position?.gridColumn === col
      );
    }; // this helper function checks if a cell should be displayed as active in the UI

    const gridStructure = resolveSetting({
      path: 'position.gridStructure',
      defaultValue: '3x3',
      mixedReturn: undefined,
    });

    const cellAlignment = resolveSetting({
      path: 'position.cellAlignment',
      defaultValue: 'center',
      mixedReturn: '',
    });

    const fontFamily = resolveSetting({
      path: 'fontFamilyName',
      defaultValue: '',
      mixedReturn: '',
    });

    const font = resolveSetting({
      path: 'style.fontFamily',
      defaultValue: '',
      mixedReturn: '',
    });

    const fontWeight = resolveSetting({
      path: 'style.fontWeight',
      defaultValue: '',
      mixedReturn: '',
    });

    const fontStretch = resolveSetting({
      path: 'style.fontStretch',
      defaultValue: '',
      mixedReturn: '',
    });

    const fontStyle = resolveSetting({
      path: 'style.fontStyle',
      defaultValue: '',
      mixedReturn: '',
    });

    function createTextColorChangeHandler({
      setting, // setting: one of 'fill' | 'highlight' | 'underline' | 'strikethrough' | 'stroke'
      settingPath, // settingPath: the path where the direct color lives, e.g.: 'style.fill', 'style.stroke', 'customStyles.highlight.color', 'customStyles.underline.color', 'customStyles.strikethrough.color'
    }) {
      return (color) => {
        const updatedColor = rgbaToHex(color.rgb); // convert color from the color picker to hex

        // 1: Handle the case when a text color entry is selected (selectedColorEntry): → update colors array/list + all references inside the selectedTextColorIndexes object of all selected text entries:
        updateDesign((prev) => {
          const idx = prev.textEditor?.selectedColorEntry; // get the index of the selected text color entry
          const colors = prev.textEditor?.colors || []; // get the colors array/list of the text editor

          if (idx == null || idx < 0 || idx >= colors.length) {
            return prev; // if no color entry is selected -> skip to the handleKeycapChange at the bottom of this function
          }

          // update the colors array/list:
          const nextColors = [...colors];
          nextColors[idx] = updatedColor;

          // update every text entry that references this color entry index in ANY setting (fill, highlight, underline, strikethrough and stroke):
          const nextKeycapData = prev.keycapData.map((row) =>
            row.map((keycap) => {
              // go through all keycaps of keycapData:
              if (!keycap || keycap.isSpacer || !keycap.texts) return keycap; // skip invalid keycaps

              const updatedTexts = Object.entries(keycap.texts).reduce(
                // go through all text entries of the current keycap:
                (acc, [k, text]) => {
                  const refs = text.selectedTextColorIndexes || {}; // get the selectedTextColorIndexes object from the current text entry
                  const refsThisIdx =
                    refs.fill === idx ||
                    refs.stroke === idx ||
                    refs.highlight === idx ||
                    refs.underline === idx ||
                    refs.strikethrough === idx; // check if any settings of the current text entry reference the selected color entry from the colors array/list

                  if (!refsThisIdx) {
                    acc[k] = text;
                    return acc; // if no settings of the current text entry reference the selected color, don't update the current text entry
                  }

                  const nextText = { ...text };

                  // style.fill / style.stroke
                  if (refs.fill === idx) {
                    nextText.style = {
                      ...(nextText.style || {}),
                      fill: updatedColor, // if the fill settings of the current text entry references the selected color entry, update its color
                    };
                  }
                  if (refs.stroke === idx) {
                    nextText.style = {
                      ...(nextText.style || {}),
                      stroke: updatedColor, // if the stroke settings of the current text entry references the selected color entry, update its color
                    };
                  }

                  // customStyles.*
                  const cs = nextText.customStyles || {};
                  if (refs.highlight === idx) {
                    cs.highlight = {
                      ...(cs.highlight || {}),
                      color: updatedColor, // if the highlight settings of the current text entry references the selected color entry, update its color
                    };
                  }
                  if (refs.underline === idx) {
                    cs.underline = {
                      ...(cs.underline || {}),
                      color: updatedColor, // if the underline settings of the current text entry references the selected color entry, update its color
                    };
                  }
                  if (refs.strikethrough === idx) {
                    cs.strikethrough = {
                      ...(cs.strikethrough || {}),
                      color: updatedColor, // if the strikethrough settings of the current text entry references the selected color entry, update its color
                    };
                  }
                  nextText.customStyles = cs;

                  acc[k] = nextText;
                  return acc;
                },
                {} // initialize accumulator as an empty object for storing updated texts
              );

              return { ...keycap, texts: updatedTexts }; // return the updated keycap with modified texts
            })
          );

          return {
            ...prev,
            textEditor: { ...prev.textEditor, colors: nextColors },
            keycapData: nextKeycapData,
          };
        });

        // 2. If no color entry was selected:
        const positionForSingle =
          selectedTag != null ? null : (selectedTextIndex ?? null); // if the user has selected a tag, use null (because no text entry can be selected while having a tag selected)

        handleKeycapChange(
          'texts',
          {
            [settingPath]: updatedColor, // apply the color directly to the settingPath of all target text entries
            [`selectedTextColorIndexes.${setting}`]: null, // remove all references to a color entry for the setting
          },
          positionForSingle
        );
      };
    }

    const handleTextColorButtonClick = (index, keycapSetting, setting) => {
      if (deleteTextColorMode) {
        // 1. Handle color deletion:
        updateDesign((prev) => {
          const deletedColorIndex = index;
          const prevSelectedEntry = prev.textEditor?.selectedColorEntry ?? null;
          const prevColors = prev.textEditor?.colors ?? [];

          const updatedColors = prevColors.filter(
            (_, i) => i !== deletedColorIndex
          ); // remove the color at the index

          const updatedKeycapData = prev.keycapData.map((row) =>
            row.map((keycap) => {
              // go through all keycaps of keycapData
              if (!keycap || keycap.isSpacer || !keycap.texts) return keycap; // skip invalid keycaps/objects

              const texts = keycap.texts;

              const next = {};
              let changed = false;
              for (const [k, t] of Object.entries(texts)) {
                // go through all text entries of the current keycap:
                if (!t) {
                  next[k] = t;
                  continue;
                }
                const refs = { ...(t.selectedTextColorIndexes || {}) }; // get the selectedTextColorIndexes from the current text entry
                let touched = false;

                for (const refKey of Object.keys(refs)) {
                  // go through all color references of the current text entry:
                  const v = refs[refKey];
                  if (v == null) continue; // if the current reference is null -> go to next
                  if (v === deletedColorIndex) {
                    refs[refKey] = null; // reset index to null if it matches the deleted one
                    touched = true;
                  } else if (v > deletedColorIndex) {
                    refs[refKey] = v - 1; // adjust index if it is greater than the deleted one
                    touched = true;
                  }
                }

                next[k] = touched
                  ? { ...t, selectedTextColorIndexes: refs }
                  : t;
                if (touched) changed = true;
              }

              return changed ? { ...keycap, texts: next } : keycap;
            })
          );

          // update the selectedColorEntry:
          const nextSelectedEntry =
            prevSelectedEntry == null
              ? null
              : prevSelectedEntry === deletedColorIndex
                ? null // reset the selectedColorEntry to null, if the color entry gets deleted
                : prevSelectedEntry > deletedColorIndex
                  ? prevSelectedEntry - 1 // if the selectedColorEntry comes after the deleted entry (inside the color list), subtract 1 from the selectedColorEntry index
                  : prevSelectedEntry;

          if (updatedColors.length === 0) toggleDeleteTextColorMode(); // auto-exit delete mode if the color array/list becomes empty

          return {
            ...prev,
            textEditor: {
              ...prev.textEditor,
              colors: updatedColors,
              selectedColorEntry: nextSelectedEntry,
            },
            keycapData: updatedKeycapData,
          };
        });
        return;
      }

      // 2. Handle selecting/deselecting a color entry:
      if (isControlDisabled) {
        // 2.1 if there are no target text entries -> toggle selectedColorEntry (UI selection):
        updateDesign((prev) => {
          const curr = prev.textEditor?.selectedColorEntry ?? null; // current selectedColorEntry
          const next = curr === index ? null : index; // toggling the selected entry

          return {
            ...prev,
            textEditor: { ...prev.textEditor, selectedColorEntry: next },
          };
        });
        return;
      }

      // 2.2 If the user has selected keycaps or a tag, modify the target text entries accordingly
      // build a flat list of targeted/selected text entries as coordinates: { rIndex, kIndex, textKey }:
      const targets = [];
      if (selectedTag != null) {
        // if the user has selected a tag -> collect all text entries that reference the selected tag:
        keycapData.forEach((row, rIndex) => {
          row.forEach((kc, kIndex) => {
            // go through all keycaps:
            if (!kc || kc.isSpacer || !kc.texts) return;
            const texts = kc.texts;

            for (const [tKey, tObj] of Object.entries(texts)) {
              // go through all text entries of the current keycap:
              if (tObj && tObj.tagId === selectedTag) {
                targets.push({ rIndex, kIndex, textKey: tKey }); // if the current text entry references the selected tag -> add it to the targets array
              }
            }
          });
        });
      } else {
        // if the user has not selected a tag -> collect all text entries of all selected keycaps (or the single selected text entry):
        const selectedSet = new Set(
          selectedKeycaps.map(
            ({ rowIndex, keycapIndex }) => `${rowIndex}:${keycapIndex}`
          )
        ); // build a fast lookup of selected keycaps ("row:keycap" -> selected)
        const oneKeycap = selectedKeycaps.length === 1; // check if the user has exactly one keycap selected

        keycapData.forEach((row, rIndex) => {
          row.forEach((kc, kIndex) => {
            // go through all keycaps of keycapData
            if (!kc || kc.isSpacer || !selectedSet.has(`${rIndex}:${kIndex}`))
              return; // skip non-selected and invalid keycaps/objects

            const texts = kc.texts || {};
            if (oneKeycap && selectedTextIndex != null) {
              // if the user has exactly one keycap and text entry selected:
              const key = String(selectedTextIndex);
              if (texts[key]) targets.push({ rIndex, kIndex, textKey: key }); // add only this one text entry to the targets array
            } else {
              // if the user has not selected a specific text entry:
              for (const key of Object.keys(texts))
                targets.push({ rIndex, kIndex, textKey: key }); // add all text entries of this keycap to the targets array
            }
          });
        });
      }

      if (targets.length === 0) return; // if no selected text entries -> nothing to do

      const allSelectedAlreadyIndex = targets.every(
        ({ rIndex, kIndex, textKey }) => {
          const text = keycapData[rIndex][kIndex].texts[textKey];
          const links = text.selectedTextColorIndexes || {};
          return links[setting] === index;
        }
      ); // check if all target text entries already reference the selected color entry

      if (allSelectedAlreadyIndex) {
        // if all selected text entries reference the selected color entry:
        handleKeycapChange(
          'texts',
          { [`selectedTextColorIndexes.${setting}`]: null }, // reset the color reference of this setting to null
          selectedTag != null ? null : selectedTextIndex
        );
      } else {
        // if not all (or no) selected text entries reference the selected color entry:
        const selectedColor = colors[index];
        if (selectedColor == null) return; // safety

        handleKeycapChange(
          'texts',
          {
            [`selectedTextColorIndexes.${setting}`]: index, // update the color reference for the setting of the current text entry to the selected color index
            [keycapSetting]: selectedColor, // apply the selected color to the setting of the current text entry
          },
          selectedTag != null ? null : selectedTextIndex
        );
      }
    };

    const settingTargets = useMemo(
      () =>
        collectTargetEntries({
          keycapData,
          entriesKey: 'texts',
          selectedKeycaps,
          selectedIndex: selectedTextIndex,
          selectedTag,
          includeSingle: true,
        }),
      [keycapData, selectedKeycaps, selectedTextIndex, selectedTag]
    ); // the text entries the settings panel edits, or the single selected one

    const majorityTextColors = useMemo(
      () => ({
        fill: pickMajorityColor(
          settingTargets,
          'style.fill',
          defaultTextObject,
          '#000000'
        ),
        stroke: pickMajorityColor(
          settingTargets,
          'style.stroke',
          defaultTextObject,
          '#000000'
        ),
        highlight: pickMajorityColor(
          settingTargets,
          'customStyles.highlight.color',
          defaultTextObject,
          '#ffff00'
        ),
        underline: pickMajorityColor(
          settingTargets,
          'customStyles.underline.color',
          defaultTextObject,
          '#000000'
        ),
        strikethrough: pickMajorityColor(
          settingTargets,
          'customStyles.strikethrough.color',
          defaultTextObject,
          '#000000'
        ),
      }),
      [settingTargets, defaultTextObject]
    ); // the colour each colour button shows: the one most target text entries use (the fallback with no target)

    const uniformTextColorInfo = useMemo(
      () =>
        uniformColorInfo(
          settingTargets,
          TEXT_COLOR_SETTINGS,
          'selectedTextColorIndexes'
        ),
      [settingTargets]
    ); // per colour setting, whether the target text entries (or the single selected one) reference the same colour of the colour list

    const showUnselectButton =
      selectedEditor === 'text' &&
      (selectedKeycaps.length || selectedTag != null);

    const unselectKeycaps = () => {
      updateDesign({
        'settings.selectedKeycaps': [],
        'settings.isoEnterSelected': false,
        'textEditor.selectedTextIndex': null,
        'textEditor.selectedTag': null,
        'iconEditor.selectedIconIndex': null,
        'iconEditor.selectedTag': null,
      });
    }; // unselect all keycaps, tags, selected text and icon entry

    const getTagColor = (id) =>
      predefinedTagColors?.[Math.max(0, (id || 1) - 1)] || '#ccc'; // helper function to get the color of a tag based on the tag id

    const handleSelectTag = (tagId) => {
      updateDesign((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          selectedKeycaps: [], // unselect all keycaps
        },
        textEditor: {
          ...prev.textEditor,
          selectedColorEntry: null, // unselect the selected color entry
          selectedTag: tagId, // set the chosen tag
          selectedTextIndex: null, // unselect specific text entry
        },
      }));

      setShowTagDropdown(false); // hide the dropdown
    };

    const handleClearSelectedEditorTag = () => {
      updateDesign({ 'textEditor.selectedTag': null }); // unselect the selected tag
      setShowTagDropdown(false); // hide the dropdown
    };

    const hasIsoEnterTextForSelectedTag = useMemo(
      () =>
        collectTargetEntries({
          keycapData,
          entriesKey: 'texts',
          selectedTag,
          isoEnterOnly: true,
        }).length > 0,
      [keycapData, selectedTag]
    ); // whether a text entry of an ISO Enter keycap references the selected tag

    function handleRenameTag(index, newName) {
      const prevTagsSnapshot = tags || []; // use current snapshot for validation
      if (index < 0 || index >= prevTagsSnapshot.length) {
        return { ok: false }; // invalid index
      }

      const { ok, reason, name } = validateTagRename(
        prevTagsSnapshot,
        index,
        newName
      ); // validate the new name
      if (!ok) {
        // if validation failed
        toast(t(TAG_NAME_KEYS[reason])); // inform the user
        return { ok: false };
      }

      if (name === (prevTagsSnapshot[index]?.name ?? '')) {
        return { ok: true, name }; // no design update needed (still report success, and echo normalized name)
      }

      // commit the rename
      updateDesign((prev) => {
        const prevTags = prev.textEditor?.tags || [];
        if (index < 0 || index >= prevTags.length) return prev; // invalid index

        const nextTags = prevTags.map((tag, i) =>
          i === index ? { ...tag, name } : tag
        ); // update the name

        return {
          ...prev,
          textEditor: {
            ...prev.textEditor,
            tags: nextTags,
          },
        };
      });

      return { ok: true, name };
    }

    if (!isActive) {
      return null; // Do not render anything if not active. (this is better than conditionally rendering the TextEditor component in the CustomizerScreen, because now this component doesn't mound and unmount every time the user switches tabs. This cause the useEffect hooks to be triggered for every mount and the states to be set back to defaults whenever it is unmounted)
    }

    return (
      <div className="text-editor-container">
        <Modal
          show={showTagManager}
          onHide={() => {
            setShowTagManager(false);
            setSelectedTagPosition(null);
          }}
          className="tag-modal"
        >
          <Modal.Header>
            <Row className="w-100">
              <Col xs={2}></Col>
              <Col xs={8}>
                <Modal.Title>
                  {t('text-editor-component-manage-tags-modal-title')}
                </Modal.Title>
              </Col>
              <Col
                xs={2}
                className="d-flex justify-content-end align-items-center"
              >
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowTagManager(false);
                    setSelectedTagPosition(null);
                  }}
                  aria-label="Close"
                ></button>
              </Col>
            </Row>
          </Modal.Header>

          <Modal.Body>
            <Form.Group className="form-group" controlId="createTag">
              <Form.Control
                type="text"
                placeholder={t(
                  'text-editor-component-manage-tags-modal-tag-name'
                )}
                className="new-tag-input customizer-mobile-device-compatible-top"
                onChange={(e) => setNewTagName(e.target.value)}
              />

              {/* Display color options as selectable buttons */}
              <div className="tag-colors-container">
                {predefinedTagColors.map((color, index) => {
                  const isColorInUse = tags.some((tag) => tag.id === index + 1); // Check if color (by index) is already assigned

                  return (
                    <button
                      key={index}
                      type="button"
                      className={`tag-color ${
                        newTagColor === color
                          ? 'selected-tag-color'
                          : 'default-tag-color'
                      } ${
                        isColorInUse
                          ? 'tag-color-in-use'
                          : 'tag-color-not-in-use'
                      }`}
                      onClick={() => setNewTagColor(color)}
                      disabled={isColorInUse} // Only allow selection if color is not in use
                      aria-pressed={newTagColor === color}
                      aria-label={`${t(
                        'text-editor-component-tag-color'
                      )} ${index + 1}`}
                      style={{
                        backgroundColor: color,
                        border:
                          newTagColor === color
                            ? '2px solid black'
                            : `2px solid ${color}`,
                      }}
                    >
                      {/* Overlay a checkmark if the color is already in use */}
                      {isColorInUse && <span>✓</span>}
                    </button>
                  );
                })}
              </div>

              <Button
                className="add-tag-button"
                onClick={() => createTag(newTagName, newTagColor)}
              >
                {t('text-editor-component-manage-tags-modal-add-tag')}
              </Button>

              <div className="tag-list-container">
                {tags.map((tag, index) => (
                  <div
                    key={index}
                    className={`tag-container ${
                      selectedTagId === tag.id ? 'selected-tag-container' : ''
                    }`}
                  >
                    <svg
                      fill="currentColor"
                      className="tag-modal-delete-svg"
                      viewBox="0 0 56 56"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => deleteTag(index)}
                      cursor="pointer"
                    >
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path d="M 44.5235 48.6602 L 46.1407 14.3945 L 48.4844 14.3945 C 49.4454 14.3945 50.2187 13.5976 50.2187 12.6367 C 50.2187 11.6758 49.4454 10.8555 48.4844 10.8555 L 38.2422 10.8555 L 38.2422 7.3398 C 38.2422 3.9883 35.9688 1.8086 32.3595 1.8086 L 23.5938 1.8086 C 19.9844 1.8086 17.7344 3.9883 17.7344 7.3398 L 17.7344 10.8555 L 7.5391 10.8555 C 6.6016 10.8555 5.7813 11.6758 5.7813 12.6367 C 5.7813 13.5976 6.6016 14.3945 7.5391 14.3945 L 9.8829 14.3945 L 11.5000 48.6836 C 11.6641 52.0586 13.8907 54.1914 17.2657 54.1914 L 38.7579 54.1914 C 42.1095 54.1914 44.3595 52.0351 44.5235 48.6602 Z M 21.4844 7.5742 C 21.4844 6.2383 22.4688 5.3008 23.8751 5.3008 L 32.1016 5.3008 C 33.5313 5.3008 34.5157 6.2383 34.5157 7.5742 L 34.5157 10.8555 L 21.4844 10.8555 Z M 17.6173 50.6758 C 16.2579 50.6758 15.2500 49.6445 15.1797 48.2852 L 13.5391 14.3945 L 42.3907 14.3945 L 40.8438 48.2852 C 40.7735 49.6680 39.7891 50.6758 38.4063 50.6758 Z M 34.9610 46.5508 C 35.7344 46.5508 36.3204 45.9180 36.3438 45.0273 L 37.0469 20.2773 C 37.0704 19.3867 36.4610 18.7305 35.6641 18.7305 C 34.9376 18.7305 34.3282 19.4102 34.3048 20.2539 L 33.6016 45.0273 C 33.5782 45.8711 34.1641 46.5508 34.9610 46.5508 Z M 21.0626 46.5508 C 21.8595 46.5508 22.4454 45.8711 22.4219 45.0273 L 21.7188 20.2539 C 21.6954 19.4102 21.0626 18.7305 20.3360 18.7305 C 19.5391 18.7305 18.9532 19.3867 18.9766 20.2773 L 19.7032 45.0273 C 19.7266 45.9180 20.2891 46.5508 21.0626 46.5508 Z M 29.4298 45.0273 L 29.4298 20.2539 C 29.4298 19.4102 28.7969 18.7305 28.0235 18.7305 C 27.2500 18.7305 26.5938 19.4102 26.5938 20.2539 L 26.5938 45.0273 C 26.5938 45.8711 27.2500 46.5508 28.0235 46.5508 C 28.7735 46.5508 29.4298 45.8711 29.4298 45.0273 Z"></path>
                      </g>
                    </svg>

                    <div className="w-100">
                      <TagNameField
                        key={tag.id}
                        tag={tag}
                        index={index}
                        onCommit={handleRenameTag}
                      />
                    </div>

                    {selectedTagId === tag.id && hoveredIndex === index ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        className="tag-modal-tag-svg"
                        cursor="pointer"
                        fill={predefinedTagColors[tag.id - 1]} // Access the color based on tag.id
                        onClick={() => {
                          if (
                            !Array.isArray(selectedKeycaps) ||
                            selectedKeycaps.length !== 1
                          )
                            return; // must have exactly one keycap selected

                          if (selectedTagPosition == null) return; // selectedTagPosition must be a valid position

                          const { rowIndex, keycapIndex } = selectedKeycaps[0];
                          const kc = keycapData?.[rowIndex]?.[keycapIndex];
                          if (!kc || kc.isSpacer) return;

                          const texts = kc.texts;
                          const key = String(selectedTagPosition);
                          const hasEntry = !!texts?.[key];
                          if (!hasEntry) return; // make sure the selected keycap has a text entry at the selectedTagPosition position

                          handleKeycapChange(
                            'texts',
                            {
                              tagId: null,
                            },
                            selectedTagPosition // the text position of the selected tag
                          );
                        }}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        <path
                          fillRule="evenodd"
                          d="M6.146 5.146a.5.5 0 0 1 .708 0L8 6.293l1.146-1.147a.5.5 0 1 1 .708.708L8.707 7l1.147 1.146a.5.5 0 0 1-.708.708L8 7.707 6.854 8.854a.5.5 0 1 1-.708-.708L7.293 7 6.146 5.854a.5.5 0 0 1 0-.708"
                        />
                        <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z" />
                      </svg>
                    ) : selectedTagId === tag.id && hoveredIndex !== index ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        className="tag-modal-tag-svg"
                        fill={predefinedTagColors[tag.id - 1]} // Access the color based on tag.id
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        <path
                          fillRule="evenodd"
                          d="M2 15.5V2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.74.439L8 13.069l-5.26 2.87A.5.5 0 0 1 2 15.5m8.854-9.646a.5.5 0 0 0-.708-.708L7.5 7.793 6.354 6.646a.5.5 0 1 0-.708.708l1.5 1.5a.5.5 0 0 0 .708 0z"
                        />
                      </svg>
                    ) : selectedTagId !== tag.id && hoveredIndex === index ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        className="tag-modal-tag-svg"
                        cursor="pointer"
                        fill={predefinedTagColors[tag.id - 1]} // Access the color based on tag.id
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        onClick={() => {
                          if (
                            !Array.isArray(selectedKeycaps) ||
                            selectedKeycaps.length !== 1
                          )
                            return; // must have exactly one keycap selected

                          if (selectedTagPosition == null) return; // selectedTagPosition must be a valid position

                          const { rowIndex, keycapIndex } = selectedKeycaps[0];
                          const kc = keycapData?.[rowIndex]?.[keycapIndex];
                          if (!kc || kc.isSpacer) return;

                          const texts = kc.texts;
                          const key = String(selectedTagPosition);
                          const hasEntry = !!texts?.[key];
                          if (!hasEntry) return; // make sure the selected keycap has a text entry at the selectedTagPosition position

                          handleKeycapChange(
                            'texts',
                            {
                              tagId: tag.id,
                            },
                            selectedTagPosition // the text position of the selected tag
                          );
                        }}
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0"
                        />
                        <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z" />
                      </svg>
                    ) : selectedTagId !== tag.id && hoveredIndex !== index ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        className="tag-modal-tag-svg"
                        fill={predefinedTagColors[tag.id - 1]} // Access the color based on tag.id
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        <path d="M2 2v13.5a.5.5 0 0 0 .74.439L8 13.069l5.26 2.87A.5.5 0 0 0 14 15.5V2a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2" />
                      </svg>
                    ) : null}
                  </div>
                ))}
              </div>
            </Form.Group>
          </Modal.Body>
        </Modal>

        <div className="text-entries-and-text-settings-wrapper">
          {/* click-away to unselect, not a control of its own — keyboard users press Escape instead (see the effect above), so the interaction rules are deliberately off here: */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className="text-entries-container"
            id="text-entries"
            onClick={(e) => {
              if (selectedTextIndex != null) {
                updateDesign({ 'textEditor.selectedTextIndex': null }); // if the user clicks somewhere inside the div (apart from the elements tha use e.stopPropagation() inside their onClick), unselect the text entry
              }
            }}
          >
            <div
              className={`text-entries-heading-container ${
                !exactlyOneSelected ||
                !keycapData?.[singleSelectedKeycap?.rowIndex]?.[
                  singleSelectedKeycap?.keycapIndex
                ]?.texts
                  ? 'disabled-text-entries-heading-container'
                  : ''
              }`}
            >
              <h3>{t('text-editor-component-text-entries')}</h3>

              {screenWidthMD && (
                <Button
                  variant="link"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent bubble to container (so that when the user triggers this onClick, it does not also trigger the onClick of the text-entries-container div)

                    setShowTextEntries(!showTextEntries);
                  }}
                  aria-expanded={showTextEntries}
                  className="toggle-settings-arrow"
                >
                  {showTextEntries ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-caret-up-fill"
                      viewBox="0 0 16 16"
                    >
                      <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-caret-down-fill"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                  )}
                </Button>
              )}
            </div>

            {(screenWidthMD && showTextEntries) || !screenWidthMD ? (
              <div className="text-entries-wrapper">
                {/* Map through text positions */}
                {exactlyOneSelected &&
                  keycapData?.[singleSelectedKeycap?.rowIndex]?.[
                    singleSelectedKeycap?.keycapIndex
                  ]?.texts &&
                  Object.keys(
                    keycapData[singleSelectedKeycap?.rowIndex][
                      singleSelectedKeycap?.keycapIndex
                    ].texts
                  ).length > 0 && // Ensure texts is defined and not empty
                  Object.entries(
                    keycapData[singleSelectedKeycap?.rowIndex][
                      singleSelectedKeycap?.keycapIndex
                    ].texts
                  ).map(
                    ([
                      position,
                      { text, style, visible, position: pos, tagId },
                    ]) => (
                      <Form.Group key={position}>
                        {/* Text Input on the left */}
                        <div className="text-entry-container">
                          <div className="delete-text-entry-container">
                            <svg
                              fill="currentColor"
                              viewBox="0 0 56 56"
                              xmlns="http://www.w3.org/2000/svg"
                              onClick={(e) => {
                                e.stopPropagation(); // prevent bubble to container (so that when the user triggers this onClick, it does not also trigger the onClick of the text-entries-container div)

                                handleKeycapChange(
                                  'texts',
                                  {},
                                  null,
                                  false,
                                  position
                                );
                              }}
                            >
                              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                              <g
                                id="SVGRepo_tracerCarrier"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              ></g>
                              <g id="SVGRepo_iconCarrier">
                                <path d="M 44.5235 48.6602 L 46.1407 14.3945 L 48.4844 14.3945 C 49.4454 14.3945 50.2187 13.5976 50.2187 12.6367 C 50.2187 11.6758 49.4454 10.8555 48.4844 10.8555 L 38.2422 10.8555 L 38.2422 7.3398 C 38.2422 3.9883 35.9688 1.8086 32.3595 1.8086 L 23.5938 1.8086 C 19.9844 1.8086 17.7344 3.9883 17.7344 7.3398 L 17.7344 10.8555 L 7.5391 10.8555 C 6.6016 10.8555 5.7813 11.6758 5.7813 12.6367 C 5.7813 13.5976 6.6016 14.3945 7.5391 14.3945 L 9.8829 14.3945 L 11.5000 48.6836 C 11.6641 52.0586 13.8907 54.1914 17.2657 54.1914 L 38.7579 54.1914 C 42.1095 54.1914 44.3595 52.0351 44.5235 48.6602 Z M 21.4844 7.5742 C 21.4844 6.2383 22.4688 5.3008 23.8751 5.3008 L 32.1016 5.3008 C 33.5313 5.3008 34.5157 6.2383 34.5157 7.5742 L 34.5157 10.8555 L 21.4844 10.8555 Z M 17.6173 50.6758 C 16.2579 50.6758 15.2500 49.6445 15.1797 48.2852 L 13.5391 14.3945 L 42.3907 14.3945 L 40.8438 48.2852 C 40.7735 49.6680 39.7891 50.6758 38.4063 50.6758 Z M 34.9610 46.5508 C 35.7344 46.5508 36.3204 45.9180 36.3438 45.0273 L 37.0469 20.2773 C 37.0704 19.3867 36.4610 18.7305 35.6641 18.7305 C 34.9376 18.7305 34.3282 19.4102 34.3048 20.2539 L 33.6016 45.0273 C 33.5782 45.8711 34.1641 46.5508 34.9610 46.5508 Z M 21.0626 46.5508 C 21.8595 46.5508 22.4454 45.8711 22.4219 45.0273 L 21.7188 20.2539 C 21.6954 19.4102 21.0626 18.7305 20.3360 18.7305 C 19.5391 18.7305 18.9532 19.3867 18.9766 20.2773 L 19.7032 45.0273 C 19.7266 45.9180 20.2891 46.5508 21.0626 46.5508 Z M 29.4298 45.0273 L 29.4298 20.2539 C 29.4298 19.4102 28.7969 18.7305 28.0235 18.7305 C 27.2500 18.7305 26.5938 19.4102 26.5938 20.2539 L 26.5938 45.0273 C 26.5938 45.8711 27.2500 46.5508 28.0235 46.5508 C 28.7735 46.5508 29.4298 45.8711 29.4298 45.0273 Z"></path>
                              </g>
                            </svg>
                          </div>

                          <div className="text-entry-input-container">
                            {visible ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                                className="eye"
                                onClick={(e) => {
                                  e.stopPropagation(); // prevent bubble to container (so that when the user triggers this onClick, it does not also trigger the onClick of the text-entries-container div)

                                  handleKeycapChange(
                                    'texts',
                                    { visible: !visible },
                                    position
                                  );
                                }} // toggle visibility when clicked
                              >
                                <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                                <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                                className="eye-slash"
                                onClick={(e) => {
                                  e.stopPropagation(); // prevent bubble to container (so that when the user triggers this onClick, it does not also trigger the onClick of the text-entries-container div)

                                  handleKeycapChange(
                                    'texts',
                                    { visible: !visible },
                                    position
                                  );
                                }} // toggle visibility when clicked
                              >
                                <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7 7 0 0 0-2.79.588l.77.771A6 6 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755q-.247.248-.517.486z" />
                                <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829" />
                                <path d="M3.35 5.47q-.27.24-.518.487A13 13 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7 7 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12z" />
                              </svg>
                            )}

                            <Form.Control
                              type="text"
                              className="customizer-mobile-device-compatible-center"
                              value={text}
                              maxLength={100}
                              style={{
                                border:
                                  selectedTextIndex === position
                                    ? '1px solid black'
                                    : '1px solid #cccccc',
                              }}
                              onClick={(e) => {
                                e.stopPropagation(); // prevent bubble to container (so that when the user triggers this onClick, it does not also trigger the onClick of the text-entries-container div)

                                if (selectedTextIndex !== position) {
                                  updateTextEditor(position); // update the text editor after the selectedTextIndex has been updated
                                }
                              }}
                              onChange={(e) => {
                                const newText = e.target.value;

                                // 1) Retrieve the loaded fontkit font from the fonts state:
                                const currentFont =
                                  fonts[fontCacheKey(style.fontFamily)];

                                if (!currentFont) {
                                  toast.warning(
                                    t(
                                      'text-editor-component-selected-font-still-loading'
                                    )
                                  );
                                  return;
                                }

                                // 2) Check for unsupported glyphs:
                                const unsupported = checkUnsupportedGlyphs(
                                  currentFont,
                                  newText
                                );

                                if (unsupported.length > 0) {
                                  // If unsupported glyphs are found:
                                  const boldGlyphs = unsupported.map(
                                    (glyph, index) => (
                                      <strong key={index}>{glyph}</strong>
                                    )
                                  ); // Format unsupported glyphs in bold for better readability

                                  // 4) Show a warning toast:
                                  toast.warning(
                                    <div>
                                      {t(
                                        'text-editor-component-following-glyphs-not-supported'
                                      )}
                                      <br />
                                      {boldGlyphs.reduce((prev, curr) => [
                                        prev,
                                        ', ',
                                        curr,
                                      ])}
                                    </div>
                                  );

                                  // 5) Return early:
                                  return;
                                }

                                // 6) If no unsupported glyphs, proceed with normal update
                                handleKeycapChange(
                                  'texts',
                                  { text: newText },
                                  position
                                );
                              }}
                              onBlur={(e) => {
                                if (!e.target.value) {
                                  // if the input field is empty on onBlur, remove the text entry from the keycap:
                                  handleKeycapChange(
                                    'texts',
                                    {},
                                    null,
                                    false,
                                    position
                                  );
                                }
                              }}
                            />
                          </div>

                          <div className="text-entry-tag-container">
                            {tagId ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill={predefinedTagColors[tagId - 1]}
                                viewBox="0 0 16 16"
                                onClick={(e) => {
                                  e.stopPropagation(); // prevent bubble to container (so that when the user triggers this onClick, it does not also trigger the onClick of the text-entries-container div)

                                  setShowTagManager(true);
                                  setSelectedTagPosition(position);
                                }}
                              >
                                <path d="M2 2v13.5a.5.5 0 0 0 .74.439L8 13.069l5.26 2.87A.5.5 0 0 0 14 15.5V2a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                                onClick={(e) => {
                                  e.stopPropagation(); // prevent bubble to container (so that when the user triggers this onClick, it does not also trigger the onClick of the text-entries-container div)

                                  setShowTagManager(true);
                                  setSelectedTagPosition(position);
                                }}
                              >
                                <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z" />
                                <path d="M8 4a.5.5 0 0 1 .5.5V6H10a.5.5 0 0 1 0 1H8.5v1.5a.5.5 0 0 1-1 0V7H6a.5.5 0 0 1 0-1h1.5V4.5A.5.5 0 0 1 8 4" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </Form.Group>
                    )
                  )}

                {exactlyOneSelected &&
                keycapData?.[singleSelectedKeycap?.rowIndex]?.[
                  singleSelectedKeycap?.keycapIndex
                ]?.texts &&
                Object.keys(
                  keycapData[singleSelectedKeycap?.rowIndex][
                    singleSelectedKeycap?.keycapIndex
                  ].texts
                ).length > 0 &&
                Object.keys(
                  keycapData[singleSelectedKeycap?.rowIndex][
                    singleSelectedKeycap?.keycapIndex
                  ].texts
                ).length < 10 ? (
                  <Button
                    variant="none"
                    className="add-text-entry-button-small"
                    disabled={
                      Object.keys(
                        keycapData[singleSelectedKeycap?.rowIndex][
                          singleSelectedKeycap?.keycapIndex
                        ].texts
                      ).length >= 10
                    }
                    onClick={(e) => {
                      e.stopPropagation(); // prevent bubble to container (so that when the user triggers this onClick, it does not also trigger the onClick of the text-entries-container div)

                      handleKeycapChange('texts', {}, null, true);
                    }}
                  >
                    +
                  </Button>
                ) : exactlyOneSelected &&
                  keycapData?.[singleSelectedKeycap?.rowIndex]?.[
                    singleSelectedKeycap?.keycapIndex
                  ]?.texts &&
                  Object.keys(
                    keycapData[singleSelectedKeycap?.rowIndex][
                      singleSelectedKeycap?.keycapIndex
                    ].texts
                  ).length === 0 ? (
                  <Button
                    variant="none"
                    className="add-text-entry-button-big"
                    disabled={
                      Object.keys(
                        keycapData[singleSelectedKeycap?.rowIndex][
                          singleSelectedKeycap?.keycapIndex
                        ].texts
                      ).length >= 10
                    }
                    onClick={(e) => {
                      e.stopPropagation(); // prevent bubble to container (so that when the user triggers this onClick, it does not also trigger the onClick of the text-entries-container div)

                      handleKeycapChange('texts', {}, null, true);
                    }}
                  >
                    +
                  </Button>
                ) : !exactlyOneSelected ||
                  !keycapData?.[singleSelectedKeycap?.rowIndex]?.[
                    singleSelectedKeycap?.keycapIndex
                  ]?.texts ? (
                  <div className="select-a-keycap-message">
                    {t('text-editor-component-select-a-keycap-message')}
                  </div>
                ) : null}
              </div>
            ) : (
              <div></div>
            )}
          </div>

          <div
            className="text-entry-settings-container"
            id="text-entry-settings"
          >
            <div ref={tagDropdownRef} className="tag-container-text-settings">
              <svg
                id="text-settings-tag"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                onClick={() => {
                  if (tags.length > 0) {
                    setShowTagDropdown((s) => !s);
                  } else {
                    toast(t('text-editor-component-no-tags-available'));
                  }
                }}
                role="button"
                aria-label="Choose tag"
              >
                <path
                  fill={selectedTag ? getTagColor(selectedTag) : '#fff'}
                  d="M2 2v13.5a.5.5 0 0 0 .74.439L8 13.069l5.26 2.87A.5.5 0 0 0 14 15.5V2a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2"
                />
                <path
                  fill={selectedTag ? getTagColor(selectedTag) : '#1a1a1a'}
                  d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z"
                />
              </svg>

              {showTagDropdown && (
                <Dropdown.Menu ref={menuRef} show className="tag-dropdown-menu">
                  {tags.map((tag) => {
                    const isCurrent = tag.id === selectedTag;
                    const currentTagColor = getTagColor(tag.id);

                    return (
                      <Dropdown.Item
                        key={tag.id}
                        active={isCurrent}
                        onClick={() => {
                          if (isCurrent) {
                            handleClearSelectedEditorTag();
                          } else {
                            handleSelectTag(tag.id);
                          }
                        }}
                        title={tag.name}
                      >
                        <span>{tag.name}</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill={currentTagColor}
                          viewBox="0 0 16 16"
                        >
                          <path d="M2 2v13.5a.5.5 0 0 0 .74.439L8 13.069l5.26 2.87A.5.5 0 0 0 14 15.5V2a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2" />
                        </svg>
                      </Dropdown.Item>
                    );
                  })}

                  {selectedTag && (
                    <>
                      <Dropdown.Divider />

                      <Dropdown.Item
                        className="remove-tag-dropdown-item"
                        onClick={handleClearSelectedEditorTag}
                      >
                        {t('text-editor-component-unselect-tag')}
                      </Dropdown.Item>
                    </>
                  )}
                </Dropdown.Menu>
              )}
            </div>

            {screenWidthXL && (
              <div className={`settings-heading-container`}>
                <h3>{t('text-editor-component-settings-heading')}</h3>

                {screenWidthMD && (
                  <Button
                    variant="link"
                    onClick={() => setShowTextSettings(!showTextSettings)}
                    aria-expanded={showTextSettings}
                    className="toggle-settings-arrow"
                  >
                    {showTextSettings ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-caret-up-fill"
                        viewBox="0 0 16 16"
                      >
                        <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-caret-down-fill"
                        viewBox="0 0 16 16"
                      >
                        <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                      </svg>
                    )}
                  </Button>
                )}
              </div>
            )}

            <div>
              {(screenWidthMD && showTextSettings) || !screenWidthMD ? (
                <div>
                  {screenWidthMD && (
                    <div
                      className={`position-heading-container ${
                        isControlDisabled ? 'disabled-text-entry-settings' : ''
                      }`}
                    >
                      <h4 className="position-heading">
                        {t('text-editor-component-position')}
                      </h4>

                      <Button
                        variant="link"
                        onClick={() =>
                          setShowPositionSettings(!showPositionSettings)
                        }
                        aria-expanded={showPositionSettings}
                        className="toggle-settings-arrow"
                      >
                        {showPositionSettings ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-caret-up-fill"
                            viewBox="0 0 16 16"
                          >
                            <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-caret-down-fill"
                            viewBox="0 0 16 16"
                          >
                            <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                          </svg>
                        )}
                      </Button>
                    </div>
                  )}

                  {(screenWidthMD && showPositionSettings) || !screenWidthMD ? (
                    <div
                      className={`${
                        isControlDisabled ? 'disabled-text-entry-settings' : ''
                      }`}
                    >
                      {/* ISO Enter Section Settings */}
                      {isoEnterSelected || hasIsoEnterTextForSelectedTag ? (
                        <div className="iso-enter-settings-container">
                          <Form.Label>
                            {t('text-editor-component-iso-enter-section')}

                            <OverlayTrigger
                              trigger="click"
                              rootClose // close the popover when the user clicks outside of it
                              placement="bottom"
                              overlay={
                                <Popover>
                                  <Popover.Header as="h3">
                                    {t(
                                      'text-editor-component-iso-enter-section-popover-1'
                                    )}
                                  </Popover.Header>
                                  <Popover.Body>
                                    {t(
                                      'text-editor-component-iso-enter-section-popover-2'
                                    )}
                                    <br />
                                    <br />-{' '}
                                    <strong>
                                      {t(
                                        'text-editor-component-iso-enter-section-popover-3'
                                      )}
                                    </strong>
                                    {t(
                                      'text-editor-component-iso-enter-section-popover-4'
                                    )}
                                    <br />
                                    <br />-{' '}
                                    <strong>
                                      {t(
                                        'text-editor-component-iso-enter-section-popover-5'
                                      )}
                                    </strong>
                                    {t(
                                      'text-editor-component-iso-enter-section-popover-6'
                                    )}
                                  </Popover.Body>
                                </Popover>
                              }
                            >
                              <Button className="info-popover">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="#707070"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                </svg>
                              </Button>
                            </OverlayTrigger>
                          </Form.Label>

                          <div className="w-100">
                            <Form.Control
                              className="customizer-mobile-device-compatible-top"
                              as="select"
                              value={resolveSetting({
                                path: 'position.isoSection',
                                defaultValue: 'top',
                                mixedReturn: '__MIXED__',
                              })}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '__MIXED__') return; // never pass the UI mixedReturn placeholder to the handleKeycapChange function (value is guaranteed to be 'top' or 'main')

                                handleKeycapChange(
                                  'texts',
                                  { 'position.isoSection': value },
                                  selectedTextIndex
                                );
                              }}
                            >
                              {/* epmty placeholder for MIXED: */}
                              <option value="__MIXED__" disabled></option>

                              <option value="top">
                                {t('text-editor-component-top')}
                              </option>
                              <option value="main">
                                {t('text-editor-component-main')}
                              </option>
                            </Form.Control>
                          </div>
                        </div>
                      ) : null}

                      {/* Grid Structure */}
                      <div className="grid-container">
                        <Nav
                          id="text-entry-grid"
                          justify
                          variant="tabs"
                          activeKey={gridStructure}
                          onSelect={(eventKey) => {
                            handleKeycapChange(
                              'texts',
                              { 'position.gridStructure': eventKey },
                              selectedTextIndex
                            );
                          }}
                        >
                          <Nav.Item title={t('text-editor-component-3x3-grid')}>
                            <Nav.Link eventKey="3x3">
                              <Grid3x3 />
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item title={t('text-editor-component-3x2-grid')}>
                            <Nav.Link eventKey="3x2">
                              <Grid3x2 />
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item title={t('text-editor-component-2x3-grid')}>
                            <Nav.Link eventKey="2x3">
                              <Grid2x3 />
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item title={t('text-editor-component-2x2-grid')}>
                            <Nav.Link eventKey="2x2">
                              <Grid2x2 />
                            </Nav.Link>
                          </Nav.Item>
                        </Nav>
                      </div>
                    </div>
                  ) : (
                    <div></div>
                  )}

                  {/* Position Section */}
                  <div
                    className={`${
                      isControlDisabled ? 'disabled-text-entry-settings' : ''
                    }`}
                  >
                    {!screenWidthMD && (
                      <div className="position-heading-container">
                        <h4 className="position-heading">
                          {t('text-editor-component-position')}
                        </h4>

                        <Button
                          variant="link"
                          onClick={() =>
                            setShowPositionSettings(!showPositionSettings)
                          }
                          aria-expanded={showPositionSettings}
                          className="toggle-settings-arrow"
                        >
                          {showPositionSettings ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-caret-up-fill"
                              viewBox="0 0 16 16"
                            >
                              <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-caret-down-fill"
                              viewBox="0 0 16 16"
                            >
                              <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                            </svg>
                          )}
                        </Button>
                      </div>
                    )}

                    {showPositionSettings && (
                      <div className="position-settings-container">
                        <div
                          id="text-entry-cell"
                          className="cell-container"
                          title={
                            getByPath(
                              commonTextSettings,
                              'position.gridStructure',
                              undefined
                            ) === MIXED
                              ? t(
                                  'text-editor-component-select-a-cell-disabled'
                                )
                              : t('text-editor-component-select-a-cell')
                          }
                        >
                          <Form.Label>
                            {t('text-editor-component-cell')}
                          </Form.Label>

                          {gridStructure === '3x3' ||
                          isMixed(
                            commonTextSettings,
                            'position.gridStructure'
                          ) ? (
                            <svg
                              className={
                                getByPath(
                                  commonTextSettings,
                                  'position.gridStructure',
                                  undefined
                                ) === MIXED
                                  ? 'disabled-setting'
                                  : ''
                              }
                              viewBox="0 0 600 600"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g id="layer1">
                                <rect
                                  style={{
                                    fill: cellIsActive(1, 1) ? '#000' : '#fff',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleSelectCell(1, 1)}
                                  id="rect1"
                                  width="170.00705"
                                  height="170.00705"
                                  x="14.996477"
                                  y="14.996477"
                                  rx="13.710619"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(1, 2) ? '#000' : '#fff',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleSelectCell(1, 2)}
                                  id="rect1-5"
                                  width="170.00705"
                                  height="170.00705"
                                  x="-385.00354"
                                  y="-185.00351"
                                  rx="13.710619"
                                  transform="scale(-1)"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(1, 3) ? '#000' : '#fff',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleSelectCell(1, 3)}
                                  id="rect1-4"
                                  width="170.00705"
                                  height="170.00705"
                                  x="414.99646"
                                  y="14.996477"
                                  rx="13.710619"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(2, 1) ? '#000' : '#fff',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleSelectCell(2, 1)}
                                  id="rect1-8"
                                  width="170.00705"
                                  height="170.00705"
                                  x="14.996477"
                                  y="214.99649"
                                  rx="13.710619"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(2, 2) ? '#000' : '#fff',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleSelectCell(2, 2)}
                                  id="rect1-5-7"
                                  width="170.00705"
                                  height="170.00705"
                                  x="214.99649"
                                  y="214.99649"
                                  rx="13.710619"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(2, 3) ? '#000' : '#fff',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleSelectCell(2, 3)}
                                  id="rect1-4-7"
                                  width="170.00705"
                                  height="170.00705"
                                  x="414.99649"
                                  y="214.99648"
                                  rx="13.710618"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(3, 1) ? '#000' : '#fff',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleSelectCell(3, 1)}
                                  id="rect1-0"
                                  width="170.00705"
                                  height="170.00705"
                                  x="14.996477"
                                  y="414.99646"
                                  rx="13.710619"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(3, 2) ? '#000' : '#fff',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleSelectCell(3, 2)}
                                  id="rect1-5-74"
                                  width="170.00705"
                                  height="170.00705"
                                  x="214.99648"
                                  y="414.99649"
                                  rx="13.710619"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(3, 3) ? '#000' : '#fff',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleSelectCell(3, 3)}
                                  id="rect1-4-8"
                                  width="170.12692"
                                  height="170.12694"
                                  x="414.93655"
                                  y="414.93655"
                                  rx="13.720286"
                                />
                              </g>
                            </svg>
                          ) : gridStructure === '3x2' ? (
                            <svg
                              viewBox="0 0 600 600"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g id="layer1">
                                <rect
                                  style={{
                                    fill: cellIsActive(1, 1) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(1, 1)}
                                  id="rect1"
                                  width="269.88684"
                                  height="169.88683"
                                  x="15.056577"
                                  y="15.056577"
                                  rx="21.765659"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(1, 2) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(1, 2)}
                                  id="rect1-5"
                                  width="269.88684"
                                  height="169.88684"
                                  x="-584.94342"
                                  y="-184.94341"
                                  rx="21.765659"
                                  transform="scale(-1)"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(2, 1) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(2, 1)}
                                  id="rect1-8"
                                  width="269.88684"
                                  height="169.88684"
                                  x="15.056575"
                                  y="215.05658"
                                  rx="21.765659"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(2, 2) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(2, 2)}
                                  id="rect1-5-7"
                                  width="269.88684"
                                  height="169.88684"
                                  x="315.05655"
                                  y="215.05658"
                                  rx="21.765659"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(3, 1) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(3, 1)}
                                  id="rect1-0"
                                  width="269.88684"
                                  height="169.88684"
                                  x="15.056575"
                                  y="415.05655"
                                  rx="21.765659"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(3, 2) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(3, 2)}
                                  id="rect1-5-74"
                                  width="269.88684"
                                  height="169.88684"
                                  x="315.05655"
                                  y="415.05655"
                                  rx="21.765659"
                                />
                              </g>
                            </svg>
                          ) : gridStructure === '2x3' ? (
                            <svg
                              viewBox="0 0 600 600"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g id="layer1">
                                <rect
                                  style={{
                                    fill: cellIsActive(1, 1) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(1, 1)}
                                  id="rect1"
                                  width="167.55452"
                                  height="267.55447"
                                  x="16.222738"
                                  y="16.222738"
                                  rx="13.51283"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(1, 2) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(1, 2)}
                                  id="rect1-5"
                                  width="167.55453"
                                  height="267.55453"
                                  x="-383.77728"
                                  y="-283.77725"
                                  rx="13.51283"
                                  transform="scale(-1)"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(1, 3) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(1, 3)}
                                  id="rect1-4"
                                  width="167.55453"
                                  height="267.55453"
                                  x="416.22272"
                                  y="16.222734"
                                  rx="13.51283"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(2, 1) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(2, 1)}
                                  id="rect1-8"
                                  width="167.55453"
                                  height="267.55453"
                                  x="16.222734"
                                  y="316.22275"
                                  rx="13.51283"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(2, 2) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(2, 2)}
                                  id="rect1-5-7"
                                  width="167.55453"
                                  height="267.55453"
                                  x="216.22275"
                                  y="316.22275"
                                  rx="13.51283"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(2, 3) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(2, 3)}
                                  id="rect1-4-7"
                                  width="167.55453"
                                  height="267.55453"
                                  x="416.22275"
                                  y="316.22275"
                                  rx="13.512829"
                                />
                              </g>
                            </svg>
                          ) : gridStructure === '2x2' ? (
                            <svg
                              viewBox="0 0 600 600"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g id="layer1">
                                <rect
                                  style={{
                                    fill: cellIsActive(1, 1) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(1, 1)}
                                  id="rect1"
                                  width="269.80493"
                                  height="269.80487"
                                  x="15.097548"
                                  y="15.097548"
                                  rx="21.759048"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(1, 2) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(1, 2)}
                                  id="rect1-5"
                                  width="269.80493"
                                  height="269.80487"
                                  x="-584.9024"
                                  y="-284.9024"
                                  rx="21.759048"
                                  transform="scale(-1)"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(2, 1) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(2, 1)}
                                  id="rect1-8"
                                  width="269.80493"
                                  height="269.80487"
                                  x="15.097548"
                                  y="315.09753"
                                  rx="21.759048"
                                />
                                <rect
                                  style={{
                                    fill: cellIsActive(2, 2) ? '#000' : '#fff',
                                    cursor: 'pointer',
                                    fillRule: 'evenodd',
                                    stroke: '#000000',
                                    strokeWidth: '20',
                                    strokeDasharray: 'none',
                                  }}
                                  onClick={() => handleSelectCell(2, 2)}
                                  id="rect1-5-7"
                                  width="269.80493"
                                  height="269.80487"
                                  x="315.09753"
                                  y="315.09753"
                                  rx="21.759048"
                                />
                              </g>
                            </svg>
                          ) : null}
                        </div>

                        {/* Cell Alignment */}
                        <div
                          id="text-entry-alignment"
                          className="alignment-container"
                          title={t('text-editor-component-align-text')}
                        >
                          <Form.Label>
                            {t('text-editor-component-alignment')}
                          </Form.Label>

                          <svg
                            viewBox="-15 -15 630 630"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              width="600"
                              height="600"
                              fill="none"
                              stroke="#000"
                              strokeWidth="20"
                              rx="13.710619"
                            />
                            {/* Bottom Right */}
                            <rect
                              style={{
                                fill:
                                  cellAlignment === 'bottomRight'
                                    ? 'black'
                                    : 'white',
                                stroke:
                                  cellAlignment === 'bottomRight'
                                    ? 'black'
                                    : 'none',
                                strokeWidth: '20',
                                cursor: 'pointer',
                              }}
                              width="172.31422"
                              height="172.31421"
                              x="413.8429"
                              y="413.8429"
                              rx="13.896686"
                              onClick={() =>
                                handleSelectCellAlignment('bottomRight')
                              }
                            />
                            {/* Bottom */}
                            <rect
                              style={{
                                fill:
                                  cellAlignment === 'bottom'
                                    ? 'black'
                                    : 'white',
                                stroke:
                                  cellAlignment === 'bottom' ? 'black' : 'none',
                                strokeWidth: '20',
                                cursor: 'pointer',
                              }}
                              width="172.31422"
                              height="172.31421"
                              x="213.84288"
                              y="413.8429"
                              rx="13.896686"
                              onClick={() =>
                                handleSelectCellAlignment('bottom')
                              }
                            />
                            {/* Bottom Left */}
                            <rect
                              style={{
                                fill:
                                  cellAlignment === 'bottomLeft'
                                    ? 'black'
                                    : 'white',
                                stroke:
                                  cellAlignment === 'bottomLeft'
                                    ? 'black'
                                    : 'none',
                                strokeWidth: '20',
                                cursor: 'pointer',
                              }}
                              width="172.31422"
                              height="172.31421"
                              x="13.842896"
                              y="413.8429"
                              rx="13.896686"
                              onClick={() =>
                                handleSelectCellAlignment('bottomLeft')
                              }
                            />
                            {/* Right */}
                            <rect
                              style={{
                                fill:
                                  cellAlignment === 'right' ? 'black' : 'white',
                                stroke:
                                  cellAlignment === 'right' ? 'black' : 'none',
                                strokeWidth: '20',
                                cursor: 'pointer',
                              }}
                              width="172.31422"
                              height="172.31421"
                              x="413.8429"
                              y="213.8429"
                              rx="13.896686"
                              onClick={() => handleSelectCellAlignment('right')}
                            />
                            {/* Center */}
                            <rect
                              style={{
                                fill:
                                  cellAlignment === 'center'
                                    ? 'black'
                                    : 'white',
                                stroke:
                                  cellAlignment === 'center' ? 'black' : 'none',
                                strokeWidth: '20',
                                cursor: 'pointer',
                              }}
                              width="172.31422"
                              height="172.31421"
                              x="213.8429"
                              y="213.8429"
                              rx="13.896686"
                              onClick={() =>
                                handleSelectCellAlignment('center')
                              }
                            />
                            {/* Left */}
                            <rect
                              style={{
                                fill:
                                  cellAlignment === 'left' ? 'black' : 'white',
                                stroke:
                                  cellAlignment === 'left' ? 'black' : 'none',
                                strokeWidth: '20',
                                cursor: 'pointer',
                              }}
                              width="172.31422"
                              height="172.31421"
                              x="13.842896"
                              y="213.8429"
                              rx="13.896686"
                              onClick={() => handleSelectCellAlignment('left')}
                            />
                            {/* Top Right */}
                            <rect
                              style={{
                                fill:
                                  cellAlignment === 'topRight'
                                    ? 'black'
                                    : 'white',
                                stroke:
                                  cellAlignment === 'topRight'
                                    ? 'black'
                                    : 'none',
                                strokeWidth: '20',
                                cursor: 'pointer',
                              }}
                              width="172.31422"
                              height="172.31421"
                              x="413.8429"
                              y="13.842896"
                              rx="13.896686"
                              onClick={() =>
                                handleSelectCellAlignment('topRight')
                              }
                            />
                            {/* Top */}
                            <rect
                              style={{
                                fill:
                                  cellAlignment === 'top' ? 'black' : 'white',
                                stroke:
                                  cellAlignment === 'top' ? 'black' : 'none',
                                strokeWidth: '20',
                                cursor: 'pointer',
                              }}
                              width="172.31422"
                              height="172.31421"
                              x="213.8429"
                              y="13.842896"
                              rx="13.896686"
                              onClick={() => handleSelectCellAlignment('top')}
                            />
                            {/* Top Left */}
                            <rect
                              style={{
                                fill:
                                  cellAlignment === 'topLeft'
                                    ? 'black'
                                    : 'white',
                                stroke:
                                  cellAlignment === 'topLeft'
                                    ? 'black'
                                    : 'none',
                                strokeWidth: '20',
                                cursor: 'pointer',
                              }}
                              width="172.31422"
                              height="172.31421"
                              x="13.842896"
                              y="13.842896"
                              rx="13.896686"
                              onClick={() =>
                                handleSelectCellAlignment('topLeft')
                              }
                            />

                            {/* Static Paths (Lines and Circle) */}
                            <path
                              d="m 452.85954,452.85956 94.28092,94.28093 m 0,0 v -47.14048 m 0,47.14048 -47.14045,-2e-5"
                              strokeWidth="16.6667"
                              stroke={
                                cellAlignment === 'bottomRight'
                                  ? 'white'
                                  : 'black'
                              }
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                handleSelectCellAlignment('bottomRight')
                              }
                            />
                            <path
                              d="m 300,433.33333 v 133.33334 m 0,0 33.33333,-33.33333 M 300,566.66667 266.66665,533.33334"
                              stroke={
                                cellAlignment === 'bottom' ? 'white' : 'black'
                              }
                              strokeWidth="16.6667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                handleSelectCellAlignment('bottom')
                              }
                            />
                            <path
                              d="m 147.14048,452.85953 -94.280913,94.28093 m 0,0 47.140463,-10e-6 m -47.140463,10e-6 -1.4e-5,-47.14047"
                              stroke={
                                cellAlignment === 'bottomLeft'
                                  ? 'white'
                                  : 'black'
                              }
                              strokeWidth="16.6667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                handleSelectCellAlignment('bottomLeft')
                              }
                            />
                            <path
                              d="m 433.33334,299.99999 h 133.33334 m 0,0 -33.33333,-33.33333 m 33.33333,33.33333 -33.33333,33.33334"
                              stroke={
                                cellAlignment === 'right' ? 'white' : 'black'
                              }
                              strokeWidth="16.6667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() => handleSelectCellAlignment('right')}
                            />
                            <circle
                              style={{
                                fill:
                                  cellAlignment === 'center'
                                    ? 'white'
                                    : 'black',
                                stroke:
                                  cellAlignment === 'center'
                                    ? 'white'
                                    : 'black',
                                strokeWidth: '3.84647',
                                cursor: 'pointer',
                              }}
                              cx="300"
                              cy="300"
                              r="23.076765"
                              onClick={() =>
                                handleSelectCellAlignment('center')
                              }
                            />
                            <path
                              d="M 166.66668,300 H 33.333332 m 0,0 33.33333,33.33333 M 33.333332,300 66.666662,266.66666"
                              stroke={
                                cellAlignment === 'left' ? 'white' : 'black'
                              }
                              strokeWidth="16.6667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() => handleSelectCellAlignment('left')}
                            />
                            <path
                              d="m 452.85954,147.14045 94.28091,-94.280912 m 0,0 -47.14045,0 m 47.14045,0 10e-6,47.140461"
                              stroke={
                                cellAlignment === 'topRight' ? 'white' : 'black'
                              }
                              strokeWidth="16.6667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                handleSelectCellAlignment('topRight')
                              }
                            />
                            <path
                              d="M 300,166.66667 V 33.333331 m 0,0 -33.33333,33.33333 M 300,33.333331 l 33.33335,33.33333"
                              stroke={
                                cellAlignment === 'top' ? 'white' : 'black'
                              }
                              strokeWidth="16.6667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() => handleSelectCellAlignment('top')}
                            />
                            <path
                              d="M 147.14046,147.14046 52.859563,52.859565 m 0,0 -1.3e-5,47.140445 m 1.3e-5,-47.140445 47.140457,-7e-6"
                              stroke={
                                cellAlignment === 'topLeft' ? 'white' : 'black'
                              }
                              strokeWidth="16.6667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                handleSelectCellAlignment('topLeft')
                              }
                            />
                          </svg>
                        </div>

                        {/* Manual Text Positioning Settings */}
                        <div
                          id="text-entry-manual-positioning"
                          className="text-position-settings-container"
                        >
                          <Row>
                            <Col className="bounding-box-container">
                              <Form.Label>
                                {t('text-editor-component-bounding-box')}

                                <OverlayTrigger
                                  trigger="click"
                                  rootClose // close the popover when the user clicks outside of it
                                  placement="bottom"
                                  overlay={
                                    <Popover>
                                      <Popover.Header as="h3">
                                        {t(
                                          'text-editor-component-bounding-box-popover-1'
                                        )}
                                      </Popover.Header>
                                      <Popover.Body>
                                        {t(
                                          'text-editor-component-bounding-box-popover-2'
                                        )}
                                        <br />
                                        <br />-{' '}
                                        <strong>
                                          {t(
                                            'text-editor-component-bounding-box-popover-3'
                                          )}
                                        </strong>
                                        {t(
                                          'text-editor-component-bounding-box-popover-4'
                                        )}
                                        <br />
                                        <br />-{' '}
                                        <strong>
                                          {t(
                                            'text-editor-component-bounding-box-popover-5'
                                          )}
                                        </strong>
                                        {t(
                                          'text-editor-component-bounding-box-popover-6'
                                        )}
                                        {/* Extended Bounding Box:
                                        <br />
                                        <br />-{' '}
                                        <strong>
                                          {t(
                                            'text-editor-component-bounding-box-popover-7'
                                          )}
                                        </strong>
                                        {t(
                                          'text-editor-component-bounding-box-popover-8'
                                        )}
                                      */}
                                      </Popover.Body>
                                    </Popover>
                                  }
                                >
                                  <Button className="info-popover">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="#707070"
                                      className="bi bi-info-circle"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                    </svg>
                                  </Button>
                                </OverlayTrigger>
                              </Form.Label>

                              <Form.Control
                                as="select"
                                className="customizer-mobile-device-compatible-top"
                                value={resolveSetting({
                                  path: 'position.boundingBox',
                                  defaultValue: 'standard',
                                  mixedReturn: '__MIXED__',
                                })}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '__MIXED__') return; // never pass the UI mixedReturn placeholder to the handleKeycapChange function (value is guaranteed to be 'compact' or 'standard')

                                  handleKeycapChange(
                                    'texts',
                                    { 'position.boundingBox': value },
                                    selectedTextIndex
                                  );
                                }}
                              >
                                {/* epmty placeholder for MIXED: */}
                                <option value="__MIXED__" disabled></option>

                                <option value="compact">
                                  {t('text-editor-component-compact')}
                                </option>
                                <option value="standard">
                                  {t('text-editor-component-standard')}
                                </option>
                                {/*
                                <option value="extended">{t('text-editor-component-extended')}</option>
                                */}
                              </Form.Control>
                            </Col>

                            <Col className="rotation-container">
                              <Form.Label>
                                {t('text-editor-component-rotation')}
                              </Form.Label>

                              <CustomInput
                                position="top"
                                value={resolveSetting({
                                  path: 'position.rotation',
                                  defaultValue: 0,
                                  mixedReturn: '__MIXED__',
                                })}
                                onChange={(e) => {
                                  const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                  if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                  const rotation = Math.max(
                                    -360,
                                    Math.min(360, Number(e.target.value))
                                  );

                                  handleKeycapChange(
                                    'texts',
                                    { 'position.rotation': rotation },
                                    selectedTextIndex
                                  );
                                }}
                                onBlur={(e) => {
                                  const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                  if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                  handleKeycapChange(
                                    'texts',
                                    {
                                      'position.rotation': parseFloat(
                                        parseFloat(e.target.value).toFixed(2)
                                      ), // Format to 2 decimal places and ensure it's stored as a number
                                    },
                                    selectedTextIndex
                                  );
                                }}
                                unitSymbol="°"
                                min="-360"
                                max="360"
                              />
                            </Col>
                          </Row>

                          <Row>
                            <Col className="x-offset-container">
                              <Form.Label>
                                {t('text-editor-component-x-offset')}
                              </Form.Label>

                              <CustomInput
                                position="top"
                                value={resolveSetting({
                                  path: 'position.xOffset',
                                  defaultValue: 0,
                                  mixedReturn: '__MIXED__',
                                })}
                                onChange={(e) => {
                                  const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                  if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                  handleKeycapChange(
                                    'texts',
                                    {
                                      'position.xOffset': Number(
                                        e.target.value
                                      ),
                                    }, // the value as a number; an emptied field returned above, as its valueAsNumber is NaN
                                    selectedTextIndex
                                  );
                                }} // Let users type freely
                                onBlur={(e) => {
                                  const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                  if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                  handleKeycapChange(
                                    'texts',
                                    {
                                      'position.xOffset': parseFloat(
                                        parseFloat(e.target.value).toFixed(2)
                                      ), // Format to 2 decimal places and ensure it's stored as a number
                                    },
                                    selectedTextIndex
                                  );
                                }}
                                unitSymbol="mm"
                                step="0.1"
                              />
                            </Col>

                            <Col className="y-offset-container">
                              <Form.Label>
                                {t('text-editor-component-y-offset')}
                              </Form.Label>

                              <CustomInput
                                position="top"
                                value={resolveSetting({
                                  path: 'position.yOffset',
                                  defaultValue: 0,
                                  mixedReturn: '__MIXED__',
                                })}
                                onChange={(e) => {
                                  const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                  if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                  handleKeycapChange(
                                    'texts',
                                    {
                                      'position.yOffset': Number(
                                        e.target.value
                                      ),
                                    }, // the value as a number; an emptied field returned above, as its valueAsNumber is NaN
                                    selectedTextIndex
                                  );
                                }} // Let users type freely
                                onBlur={(e) => {
                                  const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                  if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                  handleKeycapChange(
                                    'texts',
                                    {
                                      'position.yOffset': parseFloat(
                                        parseFloat(e.target.value).toFixed(2)
                                      ), // Format to 2 decimal places and ensure it's stored as a number
                                    },
                                    selectedTextIndex
                                  );
                                }}
                                unitSymbol="mm"
                                step="0.1"
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>
                    )}
                  </div>

                  {showPositionSettings && (
                    <div
                      id="text-entry-visual-guidelines"
                      className={`guidelines-settings-container ${
                        isControlDisabled ? 'disabled-text-entry-settings' : ''
                      }`}
                    >
                      <div className="display-grid-container">
                        <Form.Check
                          type="checkbox"
                          label={t('text-editor-component-grid')}
                          checked={showGrid}
                          onChange={() =>
                            updateDesign({
                              'settings.showGrid': (prevShowGrid) =>
                                !prevShowGrid, // toggle the value of `showGrid`
                            })
                          }
                        />
                      </div>

                      <div className="display-bounding-box-container">
                        <Form.Check
                          type="checkbox"
                          label={t('text-editor-component-bounding-box')}
                          checked={showBoundingBox}
                          onChange={() =>
                            updateDesign({
                              'settings.showBoundingBox': (
                                prevShowBoundingBox
                              ) => !prevShowBoundingBox, // toggle the value of `showBoundingBox`
                            })
                          }
                        />
                      </div>

                      {/* Printable Area is currently disabled:
                        <div className="display-printable-area-container">
                          <Form.Check
                            type="checkbox"
                            label={t('text-editor-component-printable-area')}
                            checked={showPrintableArea}
                            onChange={() =>
                              updateDesign({
                                'settings.showPrintableArea': (
                                  prevShowPrintableArea
                                ) => !prevShowPrintableArea, // toggle the value of `showPrintableArea`
                              })
                            }
                          />
                        </div>
                      */}
                    </div>
                  )}

                  <div
                    className={`customizer-divider ${
                      isControlDisabled ? 'disabled-text-entry-settings' : ''
                    }`}
                  ></div>

                  <div
                    id="text-entry-font"
                    className={`font-settings-container ${
                      isControlDisabled ? 'disabled-text-entry-settings' : ''
                    }`}
                  >
                    <div className="font-heading-container">
                      <h4>{t('text-editor-component-font')}</h4>

                      <Button
                        variant="link"
                        onClick={() => setShowFontSettings(!showFontSettings)}
                        aria-expanded={showFontSettings}
                        className="toggle-settings-arrow"
                      >
                        {showFontSettings ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-caret-up-fill"
                            viewBox="0 0 16 16"
                          >
                            <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-caret-down-fill"
                            viewBox="0 0 16 16"
                          >
                            <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                          </svg>
                        )}
                      </Button>
                    </div>

                    {showFontSettings && (
                      <div className="font-settings-wrapper">
                        <div className="font-family-and-style-wrapper">
                          <div className="font-family-container">
                            {/* Font Family */}
                            <Form.Label>
                              {t('text-editor-component-font-family')}
                            </Form.Label>

                            <div style={{ position: 'relative' }}>
                              <FormControl
                                type="text"
                                className="customizer-mobile-device-compatible-top"
                                placeholder={t('text-editor-component-search')}
                                value={fontFamilySearchQuery}
                                onChange={(e) =>
                                  setFontFamilySearchQuery(e.target.value)
                                }
                                onFocus={() => setShowFontFamilyDropdown(true)}
                                onBlur={() => {
                                  setTimeout(
                                    () => setShowFontFamilyDropdown(false),
                                    200
                                  );
                                }} // hide the dropdown after clicking away from the input
                              />

                              {/* Conditionally render the dropdown */}
                              {showFontFamilyDropdown && (
                                <Dropdown.Menu show>
                                  {sortedFontPreviews.map((font) => {
                                    const isCurrent =
                                      font.fontFamily === fontFamily;

                                    return (
                                      <Dropdown.Item
                                        className="font-family-item"
                                        key={font.fontFamily}
                                        disabled={isCurrent} // prevent selection
                                        active={isCurrent} // visually mark as current
                                        onClick={() => {
                                          if (isCurrent) return; // guard in case disabled isn’t enforced
                                          handleSelectFontFamily(
                                            font,
                                            true,
                                            false
                                          );
                                        }}
                                      >
                                        <img
                                          src={font.primaryFont.previewUrl}
                                          alt={font.fontFamily}
                                        />
                                      </Dropdown.Item>
                                    );
                                  })}
                                </Dropdown.Menu>
                              )}
                            </div>
                          </div>

                          <div className="font-style-container">
                            <Form.Label>
                              {t('text-editor-component-font-style')}
                            </Form.Label>

                            <div style={{ position: 'relative' }}>
                              <FormControl
                                type="text"
                                className={`customizer-mobile-device-compatible-top ${
                                  getByPath(
                                    commonTextSettings,
                                    'fontFamilyName',
                                    undefined
                                  ) === MIXED
                                    ? 'disabled-setting'
                                    : ''
                                }`}
                                placeholder={t('text-editor-component-search')}
                                value={fontStyleSearchQuery}
                                onChange={(e) =>
                                  setFontStyleSearchQuery(e.target.value)
                                }
                                onFocus={() => setShowFontStyleDropdown(true)}
                                onBlur={() => {
                                  setTimeout(
                                    () => setShowFontStyleDropdown(false),
                                    200
                                  );
                                }}
                              />

                              {/* Conditionally render the dropdown for font styles */}
                              {showFontStyleDropdown && (
                                <Dropdown.Menu show>
                                  {allStyles.length > 0 ? (
                                    allStyles.map((style) => {
                                      const isCurrent = style.fontName === font;

                                      return (
                                        <Dropdown.Item
                                          className="font-style-item"
                                          key={style.fontName}
                                          disabled={isCurrent}
                                          active={isCurrent}
                                          onClick={() => {
                                            if (isCurrent) return; // safety guard
                                            handleSelectFontStyle(style);
                                          }}
                                        >
                                          <img
                                            src={style.previewUrl}
                                            alt={style.fontName}
                                          />
                                          <span> ({style.style})</span>
                                        </Dropdown.Item>
                                      );
                                    })
                                  ) : (
                                    <Dropdown.Item>
                                      {t(
                                        'text-editor-component-no-styles-found'
                                      )}
                                    </Dropdown.Item>
                                  )}
                                </Dropdown.Menu>
                              )}
                            </div>

                            {/* Font wght, wdth and slnt settings: */}
                            {variationsOfSelectedFont.length > 0 &&
                              variationsOfSelectedFont.some((variation) =>
                                ['wght', 'wdth', 'slnt'].includes(variation.tag)
                              ) && (
                                <Row>
                                  {variationsOfSelectedFont.some(
                                    (variation) => variation.tag === 'wght'
                                  ) && (
                                    <Col
                                      xs={4}
                                      className="font-weight-container"
                                    >
                                      <Form.Label>
                                        {t('text-editor-component-weight')}
                                      </Form.Label>

                                      <Form.Control
                                        className="customizer-mobile-device-compatible-top"
                                        type="number"
                                        step="10"
                                        min={
                                          variationsOfSelectedFont.find(
                                            (variation) =>
                                              variation.tag === 'wght'
                                          ).minValue
                                        }
                                        max={
                                          variationsOfSelectedFont.find(
                                            (variation) =>
                                              variation.tag === 'wght'
                                          ).maxValue
                                        }
                                        value={tempFontWeight}
                                        onChange={(e) => {
                                          const newValue = parseFloat(
                                            e.target.value
                                          );
                                          setTempFontWeight(e.target.value); // Update temporary state to reflect the current input

                                          const weightVariation =
                                            variationsOfSelectedFont.find(
                                              (variation) =>
                                                variation.tag === 'wght'
                                            );

                                          // If the new value is within range, update the keycap immediately
                                          if (
                                            newValue >=
                                              weightVariation.minValue &&
                                            newValue <= weightVariation.maxValue
                                          ) {
                                            handleKeycapChange(
                                              'texts',
                                              {
                                                'style.fontWeight': newValue,
                                              },
                                              selectedTextIndex
                                            );
                                          }
                                        }}
                                        onBlur={(e) => {
                                          let newValue = parseFloat(
                                            e.target.value
                                          );
                                          const weightVariation =
                                            variationsOfSelectedFont.find(
                                              (variation) =>
                                                variation.tag === 'wght'
                                            );

                                          // Ensure the value is within the allowed range after rounding
                                          if (
                                            newValue < weightVariation.minValue
                                          ) {
                                            newValue = weightVariation.minValue;
                                          } else if (
                                            newValue > weightVariation.maxValue
                                          ) {
                                            newValue = weightVariation.maxValue;
                                          }

                                          // update the text entries, if the validated value is different from the current one:
                                          if (newValue !== fontWeight) {
                                            handleKeycapChange(
                                              'texts',
                                              {
                                                'style.fontWeight': newValue,
                                              },
                                              selectedTextIndex
                                            );
                                          }

                                          // Update the temporary value to keep the UI in sync with the keycap
                                          setTempFontWeight(newValue);
                                        }}
                                      />
                                    </Col>
                                  )}
                                  {variationsOfSelectedFont.some(
                                    (variation) => variation.tag === 'wdth'
                                  ) && (
                                    <Col
                                      xs={4}
                                      className="font-width-container"
                                    >
                                      <Form.Label>
                                        {t('text-editor-component-width')}
                                      </Form.Label>

                                      <Form.Control
                                        className="customizer-mobile-device-compatible-top"
                                        type="number"
                                        step="0.5"
                                        min={
                                          variationsOfSelectedFont.find(
                                            (variation) =>
                                              variation.tag === 'wdth'
                                          ).minValue
                                        }
                                        max={
                                          variationsOfSelectedFont.find(
                                            (variation) =>
                                              variation.tag === 'wdth'
                                          ).maxValue
                                        }
                                        value={tempFontStretch}
                                        onChange={(e) => {
                                          const newValue = parseFloat(
                                            e.target.value
                                          );
                                          setTempFontStretch(newValue); // Allow free typing and reflect current input

                                          const widthVariation =
                                            variationsOfSelectedFont.find(
                                              (variation) =>
                                                variation.tag === 'wdth'
                                            );

                                          // Update keycap if new value is within range
                                          if (
                                            newValue >=
                                              widthVariation.minValue &&
                                            newValue <= widthVariation.maxValue
                                          ) {
                                            handleKeycapChange(
                                              'texts',
                                              {
                                                'style.fontStretch': newValue,
                                              },
                                              selectedTextIndex
                                            );
                                          }
                                        }}
                                        onBlur={(e) => {
                                          let newValue = parseFloat(
                                            e.target.value
                                          );
                                          const widthVariation =
                                            variationsOfSelectedFont.find(
                                              (variation) =>
                                                variation.tag === 'wdth'
                                            );

                                          // Validate and adjust if needed
                                          if (
                                            newValue < widthVariation.minValue
                                          ) {
                                            newValue = widthVariation.minValue;
                                          } else if (
                                            newValue > widthVariation.maxValue
                                          ) {
                                            newValue = widthVariation.maxValue;
                                          }

                                          // Update keycap and temp state if adjusted
                                          if (newValue !== fontStretch) {
                                            handleKeycapChange(
                                              'texts',
                                              {
                                                'style.fontStretch': newValue,
                                              },
                                              selectedTextIndex
                                            );
                                          }

                                          // Sync temp state with final value
                                          setTempFontStretch(newValue);
                                        }}
                                      />
                                    </Col>
                                  )}
                                  {variationsOfSelectedFont.some(
                                    (variation) => variation.tag === 'slnt'
                                  ) && (
                                    <Col
                                      xs={4}
                                      className="font-slant-container"
                                    >
                                      <Form.Label>
                                        {t('text-editor-component-slant')}
                                      </Form.Label>

                                      <Form.Control
                                        className="customizer-mobile-device-compatible-top"
                                        type="number"
                                        min={
                                          variationsOfSelectedFont.find(
                                            (variation) =>
                                              variation.tag === 'slnt'
                                          ).minValue
                                        }
                                        max={
                                          variationsOfSelectedFont.find(
                                            (variation) =>
                                              variation.tag === 'slnt'
                                          ).maxValue
                                        }
                                        value={tempFontStyle}
                                        onChange={(e) => {
                                          const newValue = parseFloat(
                                            e.target.value
                                          );
                                          setTempFontStyle(newValue); // Allow typing by updating temporary state

                                          const slantVariation =
                                            variationsOfSelectedFont.find(
                                              (variation) =>
                                                variation.tag === 'slnt'
                                            );

                                          // Update keycap immediately if within range
                                          if (
                                            newValue >=
                                              slantVariation.minValue &&
                                            newValue <= slantVariation.maxValue
                                          ) {
                                            handleKeycapChange(
                                              'texts',
                                              { 'style.fontStyle': newValue },
                                              selectedTextIndex
                                            );
                                          }
                                        }}
                                        onBlur={(e) => {
                                          let newValue = parseFloat(
                                            e.target.value
                                          );
                                          const slantVariation =
                                            variationsOfSelectedFont.find(
                                              (variation) =>
                                                variation.tag === 'slnt'
                                            );

                                          // Adjust value if out of range
                                          if (
                                            newValue < slantVariation.minValue
                                          ) {
                                            newValue = slantVariation.minValue;
                                          } else if (
                                            newValue > slantVariation.maxValue
                                          ) {
                                            newValue = slantVariation.maxValue;
                                          }

                                          // Update keycap and sync temp state
                                          if (newValue !== fontStyle) {
                                            handleKeycapChange(
                                              'texts',
                                              { 'style.fontStyle': newValue },
                                              selectedTextIndex
                                            );
                                          }

                                          setTempFontStyle(newValue); // Sync temp state with final value
                                        }}
                                      />
                                    </Col>
                                  )}
                                </Row>
                              )}
                          </div>
                        </div>

                        <div className="font-size-container">
                          <Form.Label>
                            {t('text-editor-component-font-size')}

                            <OverlayTrigger
                              trigger="click"
                              rootClose // close the popover when the user clicks outside of it
                              placement="bottom"
                              overlay={
                                <Popover>
                                  <Popover.Header as="h3">
                                    {t(
                                      'text-editor-component-font-size-popover-1'
                                    )}
                                  </Popover.Header>
                                  <Popover.Body>
                                    {t(
                                      'text-editor-component-font-size-popover-2'
                                    )}
                                    <br />
                                    <br />
                                    {t(
                                      'text-editor-component-font-size-popover-3'
                                    )}
                                  </Popover.Body>
                                </Popover>
                              }
                            >
                              <Button className="info-popover">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="#707070"
                                  className="bi bi-info-circle"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                </svg>
                              </Button>
                            </OverlayTrigger>
                          </Form.Label>

                          <CustomInput
                            position="top"
                            value={resolveSetting({
                              path: 'style.fontSize',
                              defaultValue: 6,
                              mixedReturn: '__MIXED__',
                            })}
                            onChange={(e) => {
                              const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                              if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                              handleKeycapChange(
                                'texts',
                                {
                                  'style.fontSize': Number(e.target.value),
                                },
                                selectedTextIndex
                              );
                            }}
                            onBlur={(e) => {
                              const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                              if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                              const inputValue = parseFloat(e.target.value);
                              let newSize =
                                inputValue >= 0
                                  ? parseFloat(inputValue.toFixed(2))
                                  : 0; // if the value is negative, set it to 0. Otherwise round it to two decimal points

                              handleKeycapChange(
                                'texts',
                                {
                                  'style.fontSize': newSize,
                                },
                                selectedTextIndex
                              );
                            }}
                            unitSymbol="mm"
                            step="0.1"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    className={`customizer-divider ${
                      isControlDisabled ? 'disabled-text-entry-settings' : ''
                    }`}
                  ></div>

                  <div id="text-entry-style">
                    {/* Text Style Section */}
                    <div className="text-style-heading-container">
                      <h4>{t('text-editor-component-style')}</h4>

                      <Button
                        variant="link"
                        onClick={() =>
                          setShowTextStyleSettings(!showTextStyleSettings)
                        }
                        aria-expanded={showTextStyleSettings}
                        className="toggle-settings-arrow"
                      >
                        {showTextStyleSettings ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-caret-up-fill"
                            viewBox="0 0 16 16"
                          >
                            <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-caret-down-fill"
                            viewBox="0 0 16 16"
                          >
                            <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                          </svg>
                        )}
                      </Button>
                    </div>

                    {showTextStyleSettings && (
                      <div className="text-style-container">
                        {/* Text Color Button*/}
                        <div className="text-style-button-container-top">
                          <SplitButton
                            className={`${
                              isControlDisabled
                                ? 'disabled-custom-split-button'
                                : 'custom-split-button'
                            } ${
                              resolveSetting({
                                path: 'isActiveStates.fill',
                                defaultValue: false,
                                mixedReturn: false,
                              })
                                ? 'custom-split-button-enabled'
                                : ''
                            }`}
                            variant="none"
                            title={
                              <svg
                                className="text-color-svg"
                                fill="#000000"
                                viewBox="0 0 26 26"
                                version="1.1"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <defs id="defs1" />
                                <title id="title1">
                                  {t('text-editor-component-text-color')}
                                </title>
                                <path
                                  d="m 19.472238,19.885203 h 2.157414 L 14.078707,1.5471934 H 11.921293 L 4.3703484,19.885203 H 6.5277613 L 8.2644791,15.570377 H 17.692373 Z M 9.1490181,13.412964 12.859768,4.3841915 h 0.280464 l 3.678389,9.0287725 z"
                                  id="path1"
                                  style={{ strokeWidth: '0.827806' }}
                                />
                                <rect
                                  x="1.8955507"
                                  y="21.348358"
                                  width="22.208899"
                                  height="2.7088978"
                                  id="rect1"
                                  style={{
                                    strokeWidth: '0.791102',
                                    strokeDasharray: 'none',
                                  }}
                                  stroke={
                                    targets.length > 0 ||
                                    (exactlyOneSelected && selectedTextIndex)
                                      ? majorityTextColors.fill
                                      : selectedColorEntry !== null
                                        ? colors[selectedColorEntry]
                                        : '#000000'
                                  }
                                  fill={
                                    targets.length > 0 ||
                                    (exactlyOneSelected && selectedTextIndex)
                                      ? majorityTextColors.fill
                                      : selectedColorEntry !== null
                                        ? colors[selectedColorEntry]
                                        : '#000000'
                                  }
                                />
                              </svg>
                            }
                            drop="up-centered"
                            onClick={() => {
                              if (isControlDisabled) return;

                              handleKeycapChange(
                                'texts',
                                {
                                  'isActiveStates.fill': !resolveSetting({
                                    path: 'isActiveStates.fill',
                                    defaultValue: false,
                                    mixedReturn: false,
                                  }),
                                },
                                selectedTextIndex
                              );
                            }}
                          >
                            <ChromePicker
                              className={`text-editor-chrome-picker ${
                                isControlDisabled && selectedColorEntry === null
                                  ? 'disabled-text-entry-settings'
                                  : ''
                              }`}
                              color={
                                targets.length > 0 ||
                                (exactlyOneSelected && selectedTextIndex)
                                  ? majorityTextColors.fill
                                  : selectedColorEntry !== null
                                    ? colors[selectedColorEntry]
                                    : '#000000'
                              }
                              onChange={createTextColorChangeHandler({
                                setting: 'fill',
                                settingPath: 'style.fill',
                              })}
                            />

                            <div className="text-color-color-picker-container">
                              <ColorList
                                colors={colors}
                                selectedIndex={selectedColorEntry}
                                handleColorButtonClick={
                                  handleTextColorButtonClick
                                }
                                handleAddColor={handleAddTextColor}
                                deleteMode={deleteTextColorMode}
                                toggleDeleteMode={toggleDeleteTextColorMode}
                                keycapSetting={'style.fill'}
                                setting={'fill'}
                                currentColor={majorityTextColors.fill}
                                hasSelection={
                                  uniformTextColorInfo.fill.hasSelection
                                }
                                isUniformSelection={
                                  uniformTextColorInfo.fill.isUniform
                                }
                                uniformSelectedIndex={
                                  uniformTextColorInfo.fill.uniformIndex
                                }
                                showUnselectButton={showUnselectButton}
                                unselectKeycaps={unselectKeycaps}
                              />
                            </div>
                          </SplitButton>

                          {/* Highlight Color Button */}
                          <SplitButton
                            className={`${
                              isControlDisabled
                                ? 'disabled-custom-split-button'
                                : 'custom-split-button'
                            } ${
                              resolveSetting({
                                path: 'isActiveStates.highlight',
                                defaultValue: false,
                                mixedReturn: false,
                              })
                                ? 'custom-split-button-enabled'
                                : ''
                            }`}
                            variant="none"
                            title={
                              <svg
                                className="highlight-color-svg"
                                fill="currentColor"
                                viewBox="0 0 26 26"
                                version="1.1"
                                id="svg1"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <defs id="defs1" />
                                <rect
                                  x="1.8571749"
                                  y="21.284952"
                                  width="22.28565"
                                  height="3"
                                  id="rect1"
                                  style={{
                                    strokeWidth: '0.71435',
                                    strokeDasharray: 'none',
                                  }}
                                  stroke={
                                    targets.length > 0 ||
                                    (exactlyOneSelected && selectedTextIndex)
                                      ? majorityTextColors.highlight
                                      : selectedColorEntry !== null
                                        ? colors[selectedColorEntry]
                                        : '#ffff00'
                                  }
                                  fill={
                                    targets.length > 0 ||
                                    (exactlyOneSelected && selectedTextIndex)
                                      ? majorityTextColors.highlight
                                      : selectedColorEntry !== null
                                        ? colors[selectedColorEntry]
                                        : '#ffff00'
                                  }
                                />
                                <path
                                  fillRule="evenodd"
                                  d="m 16.867784,2.2483005 a 2.4604781,2.4604781 0 0 1 3.433598,0.044289 l 1.762932,1.762932 a 2.4604781,2.4604781 0 0 1 0.04306,3.4335979 L 21.599283,8.0242733 11.671254,19.090274 a 0.61511954,0.61511954 0 0 1 -0.457649,0.204219 H 7.5228878 A 0.61511954,0.61511954 0 0 1 7.235012,19.223143 l -0.5068588,0.506858 a 0.61511954,0.61511954 0 0 1 -0.435505,0.17961 H 3.8321703 A 0.61511954,0.61511954 0 0 1 3.396666,18.858989 L 5.1337634,17.12189 a 0.61511954,0.61511954 0 0 1 -0.071354,-0.287876 v -3.690716 a 0.61511954,0.61511954 0 0 1 0.20422,-0.457649 L 16.33263,2.7576195 Z M 16.726307,4.056752 6.5719129,13.167903 11.189,17.78499 20.300151,7.6305966 Z m 4.410407,2.6696189 0.07873,-0.083657 A 1.2302391,1.2302391 0 0 0 21.194535,4.9253005 L 19.431602,3.1623679 a 1.2302391,1.2302391 0 0 0 -1.717414,-0.022144 l -0.08366,0.079966 z M 9.7287063,18.064254 6.2926482,14.628197 v 1.951159 l 1.4848991,1.484898 z"
                                  id="path1"
                                  style={{ strokeWidth: '1.23024' }}
                                />
                              </svg>
                            }
                            drop="up-centered"
                            onClick={() => {
                              if (isControlDisabled) return;

                              handleKeycapChange(
                                'texts',
                                {
                                  'isActiveStates.highlight': !resolveSetting({
                                    path: 'isActiveStates.highlight',
                                    defaultValue: false,
                                    mixedReturn: false,
                                  }),
                                },
                                selectedTextIndex
                              );
                            }}
                          >
                            <ChromePicker
                              className={`text-editor-chrome-picker ${
                                isControlDisabled && selectedColorEntry === null
                                  ? 'disabled-text-entry-settings'
                                  : ''
                              }`}
                              color={
                                targets.length > 0 ||
                                (exactlyOneSelected && selectedTextIndex)
                                  ? majorityTextColors.highlight
                                  : selectedColorEntry !== null
                                    ? colors[selectedColorEntry]
                                    : '#ffff00'
                              }
                              onChange={createTextColorChangeHandler({
                                setting: 'highlight',
                                settingPath: 'customStyles.highlight.color',
                              })}
                            />

                            <div className="highligth-color-picker-container">
                              <ColorList
                                colors={colors}
                                selectedIndex={selectedColorEntry}
                                handleColorButtonClick={
                                  handleTextColorButtonClick
                                }
                                handleAddColor={handleAddTextColor}
                                deleteMode={deleteTextColorMode}
                                toggleDeleteMode={toggleDeleteTextColorMode}
                                keycapSetting={'customStyles.highlight.color'}
                                setting={'highlight'}
                                currentColor={majorityTextColors.highlight}
                                hasSelection={
                                  uniformTextColorInfo.highlight.hasSelection
                                }
                                isUniformSelection={
                                  uniformTextColorInfo.highlight.isUniform
                                }
                                uniformSelectedIndex={
                                  uniformTextColorInfo.highlight.uniformIndex
                                }
                                showUnselectButton={showUnselectButton}
                                unselectKeycaps={unselectKeycaps}
                              />
                            </div>

                            <div
                              className={`text-highlight-thickness-container ${
                                isControlDisabled
                                  ? 'disabled-text-entry-settings'
                                  : ''
                              }`}
                            >
                              <Form.Label>
                                {t('text-editor-component-thickness')}
                              </Form.Label>

                              <div className="w-100">
                                <Form.Control
                                  className="customizer-mobile-device-compatible-top"
                                  as="select"
                                  value={resolveSetting({
                                    path: 'customStyles.highlight.mode',
                                    defaultValue: 'standard',
                                    mixedReturn: '__MIXED__',
                                  })}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '__MIXED__') return; // never pass the UI mixedReturn placeholder to the handleKeycapChange function (value is guaranteed to be 'compact' or 'standard')

                                    handleKeycapChange(
                                      'texts',
                                      { 'customStyles.highlight.mode': value },
                                      selectedTextIndex
                                    );
                                  }}
                                >
                                  {/* epmty placeholder for MIXED: */}
                                  <option value="__MIXED__" disabled></option>

                                  <option value="compact">
                                    {t('text-editor-component-compact')}
                                  </option>
                                  <option value="standard">
                                    {t('text-editor-component-standard')}
                                  </option>
                                  {/* Extended Bounding Box:
                                  <option value="extended">{t('text-editor-component-extended')}</option>
                                  */}
                                </Form.Control>
                              </div>
                            </div>
                          </SplitButton>

                          {/* Underline Button */}
                          <SplitButton
                            className={`${
                              isControlDisabled
                                ? 'disabled-custom-split-button'
                                : 'custom-split-button'
                            } ${
                              resolveSetting({
                                path: 'isActiveStates.underline',
                                defaultValue: false,
                                mixedReturn: false,
                              })
                                ? 'custom-split-button-enabled'
                                : ''
                            }`}
                            variant="none"
                            title={
                              <svg
                                className="text-underline-svg"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M5.313 3.136h-1.23V9.54c0 2.105 1.47 3.623 3.917 3.623s3.917-1.518 3.917-3.623V3.136h-1.23v6.323c0 1.49-.978 2.57-2.687 2.57s-2.687-1.08-2.687-2.57zM12.5 15h-9v-1h9z" />
                              </svg>
                            }
                            drop="up-centered"
                            onClick={() => {
                              if (isControlDisabled) return;

                              handleKeycapChange(
                                'texts',
                                {
                                  'isActiveStates.underline': !resolveSetting({
                                    path: 'isActiveStates.underline',
                                    defaultValue: false,
                                    mixedReturn: false,
                                  }),
                                },
                                selectedTextIndex
                              );
                            }}
                          >
                            <ChromePicker
                              className={`text-editor-chrome-picker ${
                                isControlDisabled && selectedColorEntry === null
                                  ? 'disabled-text-entry-settings'
                                  : ''
                              }`}
                              color={
                                targets.length > 0 ||
                                (exactlyOneSelected && selectedTextIndex)
                                  ? majorityTextColors.underline
                                  : selectedColorEntry !== null
                                    ? colors[selectedColorEntry]
                                    : '#000000'
                              }
                              onChange={createTextColorChangeHandler({
                                setting: 'underline',
                                settingPath: 'customStyles.underline.color',
                              })}
                            />

                            <div className="underline-color-picker-container">
                              <ColorList
                                colors={colors}
                                selectedIndex={selectedColorEntry}
                                handleColorButtonClick={
                                  handleTextColorButtonClick
                                }
                                handleAddColor={handleAddTextColor}
                                deleteMode={deleteTextColorMode}
                                toggleDeleteMode={toggleDeleteTextColorMode}
                                keycapSetting={'customStyles.underline.color'}
                                setting={'underline'}
                                currentColor={majorityTextColors.underline}
                                hasSelection={
                                  uniformTextColorInfo.underline.hasSelection
                                }
                                isUniformSelection={
                                  uniformTextColorInfo.underline.isUniform
                                }
                                uniformSelectedIndex={
                                  uniformTextColorInfo.underline.uniformIndex
                                }
                                showUnselectButton={showUnselectButton}
                                unselectKeycaps={unselectKeycaps}
                              />
                            </div>

                            <div
                              className={`${
                                isControlDisabled
                                  ? 'disabled-text-entry-settings'
                                  : ''
                              }`}
                            >
                              <div className="underline-dimensions-container">
                                <Form.Group className="form-group">
                                  <Form.Label>
                                    {t('text-editor-component-thickness')}
                                  </Form.Label>

                                  <CustomInput
                                    position="top"
                                    value={resolveSetting({
                                      path: 'customStyles.underline.thickness',
                                      defaultValue: 0.5,
                                      mixedReturn: '__MIXED__',
                                    })}
                                    onChange={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.thickness':
                                            Number(e.target.value),
                                        }, // the value as a number; an emptied field returned above, as its valueAsNumber is NaN
                                        selectedTextIndex
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      const inputValue = parseFloat(
                                        e.target.value
                                      );
                                      let newThickness =
                                        inputValue >= 0
                                          ? parseFloat(inputValue.toFixed(2))
                                          : 0; // if the value is negative, set it to 0. Otherwise round it to two decimal points

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.thickness':
                                            newThickness,
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    unitSymbol="mm"
                                    step="0.1"
                                  />
                                </Form.Group>

                                <Form.Group className="form-group y-offset">
                                  <Form.Label>
                                    {t('text-editor-component-y-offset')}
                                  </Form.Label>

                                  <CustomInput
                                    position="top"
                                    value={resolveSetting({
                                      path: 'customStyles.underline.yOffset',
                                      defaultValue: 0.5,
                                      mixedReturn: '__MIXED__',
                                    })}
                                    onChange={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.yOffset':
                                            Number(e.target.value), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      const inputValue = parseFloat(
                                        e.target.value
                                      );

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.yOffset':
                                            parseFloat(inputValue.toFixed(2)),
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    unitSymbol="mm"
                                    step="0.1"
                                  />
                                </Form.Group>

                                <Form.Group className="form-group">
                                  <Form.Label>
                                    {t(
                                      'text-editor-component-width-adjustment'
                                    )}
                                  </Form.Label>

                                  <CustomInput
                                    position="top"
                                    value={resolveSetting({
                                      path: 'customStyles.underline.widthAdjustment',
                                      defaultValue: 0,
                                      mixedReturn: '__MIXED__',
                                    })}
                                    onChange={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.widthAdjustment':
                                            Number(e.target.value), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      const inputValue = parseFloat(
                                        e.target.value
                                      );

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.widthAdjustment':
                                            parseFloat(inputValue.toFixed(2)),
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    unitSymbol="mm"
                                    step="0.1"
                                  />
                                </Form.Group>
                              </div>

                              <div className="underline-line-style-container">
                                <Form.Group className="form-group">
                                  <Form.Select
                                    className="customizer-mobile-device-compatible-top"
                                    value={resolveSetting({
                                      path: 'customStyles.underline.lineStyle',
                                      defaultValue: 'solid',
                                      mixedReturn: '__MIXED__',
                                    })}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === '__MIXED__') return; // never pass the UI mixedReturn placeholder to the handleKeycapChange function (value is guaranteed to be 'solid', 'dashed' or 'dotted')

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.lineStyle':
                                            value,
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                  >
                                    {/* epmty placeholder for MIXED: */}
                                    <option value="__MIXED__" disabled></option>

                                    <option value="solid">
                                      {t('text-editor-component-solid')}
                                    </option>
                                    <option value="dashed">
                                      {t('text-editor-component-dashed')}
                                    </option>
                                    <option value="dotted">
                                      {t('text-editor-component-dotted')}
                                    </option>
                                  </Form.Select>
                                </Form.Group>
                              </div>

                              <div
                                className={`underline-configuration-container ${
                                  isMixed(
                                    commonTextSettings,
                                    'customStyles.underline.lineStyle'
                                  )
                                    ? 'disabled-text-entry-settings'
                                    : ''
                                }`}
                              >
                                <Form.Group className="form-group">
                                  <Form.Label>
                                    {t('text-editor-component-line-cap')}
                                  </Form.Label>

                                  <Form.Select
                                    className="custom-select customizer-mobile-device-compatible-top"
                                    value={resolveSetting({
                                      path: 'customStyles.underline.lineCap',
                                      defaultValue: 'butt',
                                      mixedReturn: '__MIXED__',
                                    })}
                                    disabled={
                                      resolveSetting({
                                        path: 'customStyles.underline.lineStyle',
                                        defaultValue: 'solid',
                                        mixedReturn: '__MIXED__',
                                      }) === 'dotted'
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === '__MIXED__') return; // never pass the UI mixedReturn placeholder to the handleKeycapChange function (value is guaranteed to be 'butt' or 'round')

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.lineCap':
                                            value,
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                  >
                                    {/* epmty placeholder for MIXED: */}
                                    <option value="__MIXED__" disabled></option>

                                    <option value="butt">
                                      {t('text-editor-component-butt')}
                                    </option>
                                    <option value="round">
                                      {t('text-editor-component-round')}
                                    </option>
                                  </Form.Select>
                                </Form.Group>

                                <Form.Group className="form-group">
                                  <Form.Label>
                                    {t('text-editor-component-gap-length')}
                                  </Form.Label>

                                  <CustomInput
                                    position="top"
                                    value={resolveSetting({
                                      path: 'customStyles.underline.gapLength',
                                      defaultValue: 2,
                                      mixedReturn: '__MIXED__',
                                    })}
                                    disabled={
                                      resolveSetting({
                                        path: 'customStyles.underline.lineStyle',
                                        defaultValue: 'solid',
                                        mixedReturn: '__MIXED__',
                                      }) === 'solid'
                                    }
                                    onChange={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.gapLength':
                                            Number(e.target.value), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      const inputValue = parseFloat(
                                        e.target.value
                                      );
                                      let newLength =
                                        inputValue >= 0
                                          ? parseFloat(inputValue.toFixed(2))
                                          : 0; // if the value is negative, set it to 0. Otherwise round it to two decimal points

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.gapLength':
                                            newLength,
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    unitSymbol="mm"
                                    step="0.1"
                                  />
                                </Form.Group>

                                <Form.Group className="form-group">
                                  <Form.Label>
                                    {t('text-editor-component-dash-length')}
                                  </Form.Label>

                                  <CustomInput
                                    position="top"
                                    value={resolveSetting({
                                      path: 'customStyles.underline.dashLength',
                                      defaultValue: 4,
                                      mixedReturn: '__MIXED__',
                                    })}
                                    disabled={
                                      resolveSetting({
                                        path: 'customStyles.underline.lineStyle',
                                        defaultValue: 'solid',
                                        mixedReturn: '__MIXED__',
                                      }) === 'solid' ||
                                      resolveSetting({
                                        path: 'customStyles.underline.lineStyle',
                                        defaultValue: 'solid',
                                        mixedReturn: '__MIXED__',
                                      }) === 'dotted'
                                    }
                                    onChange={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.dashLength':
                                            Number(e.target.value), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      const inputValue = parseFloat(
                                        e.target.value
                                      );
                                      let newLength =
                                        inputValue >= 0
                                          ? parseFloat(inputValue.toFixed(2))
                                          : 0; // if the value is negative, set it to 0. Otherwise round it to two decimal points

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.underline.dashLength':
                                            newLength,
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    unitSymbol="mm"
                                    step="0.1"
                                  />
                                </Form.Group>
                              </div>
                            </div>
                          </SplitButton>
                        </div>

                        {/* Strikethrough Button */}
                        <div className="text-style-button-container-bottom">
                          <SplitButton
                            className={`${
                              isControlDisabled
                                ? 'disabled-custom-split-button'
                                : 'custom-split-button'
                            } ${
                              resolveSetting({
                                path: 'isActiveStates.strikethrough',
                                defaultValue: false,
                                mixedReturn: false,
                              })
                                ? 'custom-split-button-enabled'
                                : ''
                            }`}
                            variant="none"
                            title={
                              <svg
                                className="text-strikethrough-svg"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M6.333 5.686c0 .31.083.581.27.814H5.166a2.8 2.8 0 0 1-.099-.76c0-1.627 1.436-2.768 3.48-2.768 1.969 0 3.39 1.175 3.445 2.85h-1.23c-.11-1.08-.964-1.743-2.25-1.743-1.23 0-2.18.602-2.18 1.607zm2.194 7.478c-2.153 0-3.589-1.107-3.705-2.81h1.23c.144 1.06 1.129 1.703 2.544 1.703 1.34 0 2.31-.705 2.31-1.675 0-.827-.547-1.374-1.914-1.675L8.046 8.5H1v-1h14v1h-3.504c.468.437.675.994.675 1.697 0 1.826-1.436 2.967-3.644 2.967" />
                              </svg>
                            }
                            drop="up-centered"
                            onClick={() => {
                              if (isControlDisabled) return;

                              handleKeycapChange(
                                'texts',
                                {
                                  'isActiveStates.strikethrough':
                                    !resolveSetting({
                                      path: 'isActiveStates.strikethrough',
                                      defaultValue: false,
                                      mixedReturn: false,
                                    }),
                                },
                                selectedTextIndex
                              );
                            }}
                          >
                            <ChromePicker
                              className={`text-editor-chrome-picker ${
                                isControlDisabled && selectedColorEntry === null
                                  ? 'disabled-text-entry-settings'
                                  : ''
                              }`}
                              color={
                                targets.length > 0 ||
                                (exactlyOneSelected && selectedTextIndex)
                                  ? majorityTextColors.strikethrough
                                  : selectedColorEntry !== null
                                    ? colors[selectedColorEntry]
                                    : '#000000'
                              }
                              onChange={createTextColorChangeHandler({
                                setting: 'strikethrough',
                                settingPath: 'customStyles.strikethrough.color',
                              })}
                            />

                            <div className="strikethrough-color-picker-container">
                              <ColorList
                                colors={colors}
                                selectedIndex={selectedColorEntry}
                                handleColorButtonClick={
                                  handleTextColorButtonClick
                                }
                                handleAddColor={handleAddTextColor}
                                deleteMode={deleteTextColorMode}
                                toggleDeleteMode={toggleDeleteTextColorMode}
                                keycapSetting={
                                  'customStyles.strikethrough.color'
                                }
                                setting={'strikethrough'}
                                currentColor={majorityTextColors.strikethrough}
                                hasSelection={
                                  uniformTextColorInfo.strikethrough
                                    .hasSelection
                                }
                                isUniformSelection={
                                  uniformTextColorInfo.strikethrough.isUniform
                                }
                                uniformSelectedIndex={
                                  uniformTextColorInfo.strikethrough
                                    .uniformIndex
                                }
                                showUnselectButton={showUnselectButton}
                                unselectKeycaps={unselectKeycaps}
                              />
                            </div>

                            <div
                              className={`${
                                isControlDisabled
                                  ? 'disabled-text-entry-settings'
                                  : ''
                              }`}
                            >
                              <div className="strikethrough-dimensions-container">
                                <Form.Group className="form-group">
                                  <Form.Label>
                                    {t('text-editor-component-thickness')}
                                  </Form.Label>

                                  <CustomInput
                                    position="top"
                                    value={resolveSetting({
                                      path: 'customStyles.strikethrough.thickness',
                                      defaultValue: 0.5,
                                      mixedReturn: '__MIXED__',
                                    })}
                                    onChange={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.thickness':
                                            Number(e.target.value), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      const inputValue = parseFloat(
                                        e.target.value
                                      );
                                      let newThickness =
                                        inputValue >= 0
                                          ? parseFloat(inputValue.toFixed(2))
                                          : 0; // if the value is negative, set it to 0. Otherwise round it to two decimal points

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.thickness':
                                            newThickness,
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    unitSymbol="mm"
                                    step="0.1"
                                  />
                                </Form.Group>

                                <Form.Group className="form-group">
                                  <Form.Label>
                                    {t('text-editor-component-y-offset')}
                                  </Form.Label>

                                  <CustomInput
                                    position="top"
                                    value={resolveSetting({
                                      path: 'customStyles.strikethrough.yOffset',
                                      defaultValue: 0,
                                      mixedReturn: '__MIXED__',
                                    })}
                                    onChange={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.yOffset':
                                            Number(e.target.value), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      const inputValue = parseFloat(
                                        e.target.value
                                      );

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.yOffset':
                                            parseFloat(inputValue.toFixed(2)),
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    unitSymbol="mm"
                                    step="0.1"
                                  />
                                </Form.Group>

                                <Form.Group className="form-group">
                                  <Form.Label>
                                    {t(
                                      'text-editor-component-width-adjustment'
                                    )}
                                  </Form.Label>

                                  <CustomInput
                                    position="top"
                                    value={resolveSetting({
                                      path: 'customStyles.strikethrough.widthAdjustment',
                                      defaultValue: 0,
                                      mixedReturn: '__MIXED__',
                                    })}
                                    onChange={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.widthAdjustment':
                                            Number(e.target.value), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      const inputValue = parseFloat(
                                        e.target.value
                                      );

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.widthAdjustment':
                                            parseFloat(inputValue.toFixed(2)),
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    unitSymbol="mm"
                                    step="0.1"
                                  />
                                </Form.Group>
                              </div>

                              <div className="strikethrough-line-style-container">
                                <Form.Group className="form-group">
                                  <Form.Select
                                    className="customizer-mobile-device-compatible-top"
                                    value={resolveSetting({
                                      path: 'customStyles.strikethrough.lineStyle',
                                      defaultValue: 'solid',
                                      mixedReturn: '__MIXED__',
                                    })}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === '__MIXED__') return; // never pass the UI mixedReturn placeholder to the handleKeycapChange function (value is guaranteed to be 'solid', 'dashed' or 'dotted')

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.lineStyle':
                                            value,
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                  >
                                    {/* epmty placeholder for MIXED: */}
                                    <option value="__MIXED__" disabled></option>

                                    <option value="solid">
                                      {t('text-editor-component-solid')}
                                    </option>
                                    <option value="dashed">
                                      {t('text-editor-component-dashed')}
                                    </option>
                                    <option value="dotted">
                                      {t('text-editor-component-dotted')}
                                    </option>
                                  </Form.Select>
                                </Form.Group>
                              </div>

                              <div
                                className={`strikethrough-configuration-container ${
                                  isMixed(
                                    commonTextSettings,
                                    'customStyles.strikethrough.lineStyle'
                                  )
                                    ? 'disabled-text-entry-settings'
                                    : ''
                                }`}
                              >
                                <Form.Group className="form-group">
                                  <Form.Label>
                                    {t('text-editor-component-line-cap')}
                                  </Form.Label>

                                  <Form.Select
                                    className="custom-select customizer-mobile-device-compatible-top"
                                    value={resolveSetting({
                                      path: 'customStyles.strikethrough.lineCap',
                                      defaultValue: 'butt',
                                      mixedReturn: '__MIXED__',
                                    })}
                                    disabled={
                                      resolveSetting({
                                        path: 'customStyles.strikethrough.lineStyle',
                                        defaultValue: 'solid',
                                        mixedReturn: '__MIXED__',
                                      }) === 'dotted'
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === '__MIXED__') return; // never pass the UI mixedReturn placeholder to the handleKeycapChange function (value is guaranteed to be 'butt' or 'round')

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.lineCap':
                                            value,
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                  >
                                    {/* epmty placeholder for MIXED: */}
                                    <option value="__MIXED__" disabled></option>

                                    <option value="butt">
                                      {t('text-editor-component-butt')}
                                    </option>
                                    <option value="round">
                                      {t('text-editor-component-round')}
                                    </option>
                                  </Form.Select>
                                </Form.Group>

                                <Form.Group className="form-group">
                                  <Form.Label>
                                    {t('text-editor-component-gap-length')}
                                  </Form.Label>

                                  <CustomInput
                                    position="top"
                                    value={resolveSetting({
                                      path: 'customStyles.strikethrough.gapLength',
                                      defaultValue: 2,
                                      mixedReturn: '__MIXED__',
                                    })}
                                    disabled={
                                      resolveSetting({
                                        path: 'customStyles.strikethrough.lineStyle',
                                        defaultValue: 'solid',
                                        mixedReturn: '__MIXED__',
                                      }) === 'solid'
                                    }
                                    onChange={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.gapLength':
                                            Number(e.target.value), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      const inputValue = parseFloat(
                                        e.target.value
                                      );
                                      let newLength =
                                        inputValue >= 0
                                          ? parseFloat(inputValue.toFixed(2))
                                          : 0; // if the value is negative, set it to 0. Otherwise round it to two decimal points

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.gapLength':
                                            newLength,
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    unitSymbol="mm"
                                    step="0.1"
                                  />
                                </Form.Group>

                                <Form.Group className="form-group">
                                  <Form.Label>
                                    {t('text-editor-component-dash-length')}
                                  </Form.Label>

                                  <CustomInput
                                    position="top"
                                    value={resolveSetting({
                                      path: 'customStyles.strikethrough.dashLength',
                                      defaultValue: 4,
                                      mixedReturn: '__MIXED__',
                                    })}
                                    disabled={
                                      resolveSetting({
                                        path: 'customStyles.strikethrough.lineStyle',
                                        defaultValue: 'solid',
                                        mixedReturn: '__MIXED__',
                                      }) === 'solid' ||
                                      resolveSetting({
                                        path: 'customStyles.strikethrough.lineStyle',
                                        defaultValue: 'solid',
                                        mixedReturn: '__MIXED__',
                                      }) === 'dotted'
                                    }
                                    onChange={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.dashLength':
                                            Number(e.target.value), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const number =
                                        e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                      if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                      const inputValue = parseFloat(
                                        e.target.value
                                      );
                                      let newLength =
                                        inputValue >= 0
                                          ? parseFloat(inputValue.toFixed(2))
                                          : 0; // if the value is negative, set it to 0. Otherwise round it to two decimal points

                                      handleKeycapChange(
                                        'texts',
                                        {
                                          'customStyles.strikethrough.dashLength':
                                            newLength,
                                        },
                                        selectedTextIndex
                                      );
                                    }}
                                    unitSymbol="mm"
                                    step="0.1"
                                  />
                                </Form.Group>
                              </div>
                            </div>
                          </SplitButton>

                          {/* Stroke Button */}
                          <SplitButton
                            className={`${
                              isControlDisabled
                                ? 'disabled-custom-split-button'
                                : 'custom-split-button'
                            } ${
                              resolveSetting({
                                path: 'isActiveStates.stroke',
                                defaultValue: false,
                                mixedReturn: false,
                              })
                                ? 'custom-split-button-enabled'
                                : ''
                            }`}
                            variant="none"
                            title={
                              <svg
                                className="text-stroke-svg"
                                viewBox="0 0 16 16"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <defs />
                                <path
                                  d="m 5.1655988,10.748746 q 0.062295,0.451636 0.3036859,0.817616 0.2413913,0.36598 0.6229453,0.622945 0.3893408,0.256965 0.9032707,0.397128 0.5139299,0.140163 1.1290884,0.140163 0.5840112,0 1.0745806,-0.14795 0.4905695,-0.147949 0.8487633,-0.412701 0.36598,-0.264752 0.568437,-0.630732 0.202458,-0.365981 0.202458,-0.794256 0,-0.739747 -0.560651,-1.2225297 Q 9.7053128,9.0356466 8.5061431,8.7397476 L 7.0344348,8.3581936 Q 6.2479663,8.1635232 5.6795287,7.8831978 5.1188779,7.6028724 4.7528975,7.2213184 4.394704,6.8397644 4.2156072,6.3569818 q -0.17131,-0.4905695 -0.17131,-1.105728 0,-0.7319607 0.2881123,-1.3393324 Q 4.6283085,3.3045497 5.1655988,2.8762748 5.7028891,2.4402131 6.4504235,2.2066086 7.2057447,1.9652173 8.1168022,1.9652173 q 0.8331894,0 1.5495765,0.2413913 0.7163873,0.2336045 1.2458903,0.6540926 0.529504,0.4204881 0.848763,1.0044993 0.31926,0.5840112 0.365981,1.2848247 H 10.678665 Q 10.608584,4.7061767 10.390553,4.3635568 10.180309,4.01315 9.8376887,3.7717587 9.5028556,3.5225806 9.0512202,3.3979915 8.6073717,3.2656156 8.0700813,3.2656156 q -0.5762244,0 -1.0512202,0.1401627 Q 6.5516521,3.545941 6.216819,3.802906 5.8897727,4.0520841 5.7028891,4.4102777 5.5237924,4.7684712 5.5237924,5.2045329 q 0,0.3348332 0.124589,0.6073717 0.1245891,0.2647518 0.3893409,0.4905695 0.2647517,0.2180308 0.6852398,0.3971276 0.4204881,0.1790968 1.020073,0.3270463 l 1.2069565,0.3192595 q 0.8954839,0.2258176 1.5262164,0.5217167 0.630732,0.2881122 1.035646,0.677453 0.404915,0.3893408 0.591798,0.8876971 0.186884,0.4983562 0.186884,1.1368747 0,0.794256 -0.303686,1.440562 -0.295899,0.638518 -0.85655,1.090154 -0.560651,0.443848 -1.3549058,0.68524 -0.7942553,0.249178 -1.783181,0.249178 -0.9188444,0 -1.6819524,-0.233605 Q 5.5471528,13.567574 4.986502,13.139299 4.4258512,12.711024 4.0910181,12.103652 3.7639719,11.496281 3.7094641,10.748746 Z"
                                  fill="#ffffff"
                                  stroke="#000000"
                                  strokeWidth="0.5"
                                  fillRule="evenodd"
                                />
                              </svg>
                            }
                            drop="up-centered"
                            onClick={() => {
                              if (isControlDisabled) return;

                              handleKeycapChange(
                                'texts',
                                {
                                  'isActiveStates.stroke': !resolveSetting({
                                    path: 'isActiveStates.stroke',
                                    defaultValue: false,
                                    mixedReturn: false,
                                  }),
                                },
                                selectedTextIndex
                              );
                            }}
                          >
                            <ChromePicker
                              className={`text-editor-chrome-picker ${
                                isControlDisabled && selectedColorEntry === null
                                  ? 'disabled-text-entry-settings'
                                  : ''
                              }`}
                              color={
                                targets.length > 0 ||
                                (exactlyOneSelected && selectedTextIndex)
                                  ? majorityTextColors.stroke
                                  : selectedColorEntry !== null
                                    ? colors[selectedColorEntry]
                                    : '#000000'
                              }
                              onChange={createTextColorChangeHandler({
                                setting: 'stroke',
                                settingPath: 'style.stroke',
                              })}
                            />

                            <div className="stroke-color-picker-container">
                              <ColorList
                                colors={colors}
                                selectedIndex={selectedColorEntry}
                                handleColorButtonClick={
                                  handleTextColorButtonClick
                                }
                                handleAddColor={handleAddTextColor}
                                deleteMode={deleteTextColorMode}
                                toggleDeleteMode={toggleDeleteTextColorMode}
                                keycapSetting={'style.stroke'}
                                setting={'stroke'}
                                currentColor={majorityTextColors.stroke}
                                hasSelection={
                                  uniformTextColorInfo.stroke.hasSelection
                                }
                                isUniformSelection={
                                  uniformTextColorInfo.stroke.isUniform
                                }
                                uniformSelectedIndex={
                                  uniformTextColorInfo.stroke.uniformIndex
                                }
                                showUnselectButton={showUnselectButton}
                                unselectKeycaps={unselectKeycaps}
                              />
                            </div>

                            <div
                              className={`stroke-thickness-container ${
                                isControlDisabled
                                  ? 'disabled-text-entry-settings'
                                  : ''
                              }`}
                            >
                              <Form.Label>
                                {t('text-editor-component-stroke-width')}
                              </Form.Label>

                              <CustomInput
                                position="top"
                                value={resolveSetting({
                                  path: 'style.strokeWidth',
                                  defaultValue: 0.5,
                                  mixedReturn: '__MIXED__',
                                })}
                                onChange={(e) => {
                                  const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                  if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                  handleKeycapChange(
                                    'texts',
                                    {
                                      'style.strokeWidth': Number(
                                        e.target.value
                                      ), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                    },
                                    selectedTextIndex
                                  );
                                }}
                                onBlur={(e) => {
                                  const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                  if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                  const inputValue = parseFloat(e.target.value);
                                  let newThickness =
                                    inputValue >= 0
                                      ? parseFloat(inputValue.toFixed(2))
                                      : 0; // if the value is negative, set it to 0. Otherwise round it to two decimal points

                                  handleKeycapChange(
                                    'texts',
                                    {
                                      'style.strokeWidth': newThickness,
                                    },
                                    selectedTextIndex
                                  );
                                }}
                                unitSymbol="mm"
                                step="0.1"
                              />
                            </div>
                          </SplitButton>

                          {/* Text Spacing Button */}
                          <SplitButton
                            className={`custom-split-button ${
                              isControlDisabled
                                ? 'disabled-text-entry-settings'
                                : ''
                            } ${
                              resolveSetting({
                                path: 'isActiveStates.textSpacing',
                                defaultValue: false,
                                mixedReturn: false,
                              })
                                ? 'custom-split-button-enabled'
                                : ''
                            }`}
                            variant="none"
                            title={
                              <svg
                                className="text-spacing-svg"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="m 2.073 13.046 a 0.435 0.354 0 0 1 0 -0.502 l 1.738 -1.417 a 0.435 0.355 0 0 1 0.615 0.502 l -0.997 0.812 h 9.2 l -0.997 -0.812 a 0.435 0.355 0 1 1 0.615 -0.502 l 1.738 1.417 a 0.435 0.354 0 0 1 0 0.502 l -1.738 1.417 a 0.435 0.355 0 0 1 -0.615 -0.502 l 0.997 -0.812 H 3.43 l 0.997 0.812 a 0.435 0.355 0 0 1 -0.615 0.502 z"
                                  fill="#000"
                                />
                                <path
                                  d="M 8.379 10.246 H 7.341 L 6.623 8.205 H 3.456 L 2.738 10.246 H 1.75 L 4.415 2.924 h 1.298 z M 6.323 7.369 5.04 3.774 3.751 7.369 Z m 7.927 0.089 q 0 0.688 -0.197 1.239 -0.192 0.551 -0.521 0.924 -0.349 0.388 -0.767 0.585 -0.418 0.192 -0.92 0.192 -0.467 0 -0.816 -0.113 -0.349 -0.108 -0.688 -0.295 l -0.059 0.256 H 9.416 V 2.594 h 0.924 v 2.734 q 0.388 -0.32 0.826 -0.521 0.438 -0.207 0.983 -0.207 0.974 0 1.534 0.747 0.565 0.747 0.565 2.11 z m -0.954 0.025 q 0 -0.983 -0.325 -1.49 -0.325 -0.511 -1.047 -0.511 -0.403 0 -0.816 0.177 -0.413 0.172 -0.767 0.447 v 3.147 q 0.393 0.177 0.674 0.246 0.285 0.069 0.644 0.069 0.767 0 1.2 -0.502 0.438 -0.506 0.438 -1.583 z"
                                  fill="#000"
                                  fontSize="10.07"
                                  fontFamily="'Noto Sans Inscriptional Pahlavi'"
                                  textAnchor="middle"
                                  aria-label="Ab"
                                />
                              </svg>
                            }
                            drop="up-centered"
                            onClick={() => {
                              handleKeycapChange(
                                'texts',
                                {
                                  'isActiveStates.textSpacing': !resolveSetting(
                                    {
                                      path: 'isActiveStates.textSpacing',
                                      defaultValue: false,
                                      mixedReturn: false,
                                    }
                                  ),
                                },
                                selectedTextIndex
                              );
                            }}
                          >
                            <div className="text-spacing-settings-container">
                              <Form.Group className="form-group">
                                <Form.Label>
                                  {t('text-editor-component-letter-spacing')}

                                  <OverlayTrigger
                                    trigger="click"
                                    rootClose // close the popover when the user clicks outside of it
                                    placement="top"
                                    overlay={
                                      <Popover>
                                        <Popover.Header as="h3">
                                          {t(
                                            'text-editor-component-letter-spacing-popover-1'
                                          )}
                                        </Popover.Header>
                                        <Popover.Body>
                                          {t(
                                            'text-editor-component-letter-spacing-popover-2'
                                          )}
                                          <br />
                                          <br />
                                          {t(
                                            'text-editor-component-letter-spacing-popover-3'
                                          )}
                                        </Popover.Body>
                                      </Popover>
                                    }
                                  >
                                    <Button className="info-popover">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="#707070"
                                        className="bi bi-info-circle"
                                        viewBox="0 0 16 16"
                                      >
                                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                      </svg>
                                    </Button>
                                  </OverlayTrigger>
                                </Form.Label>

                                <CustomInput
                                  position="top"
                                  value={resolveSetting({
                                    path: 'style.letterSpacing',
                                    defaultValue: 0,
                                    mixedReturn: '__MIXED__',
                                  })}
                                  onChange={(e) => {
                                    const number =
                                      e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                    if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                    handleKeycapChange(
                                      'texts',
                                      {
                                        'style.letterSpacing': Number(
                                          e.target.value
                                        ), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                      },
                                      selectedTextIndex
                                    );
                                  }}
                                  onBlur={(e) => {
                                    const number =
                                      e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                    if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                    const inputValue = parseFloat(
                                      e.target.value
                                    );

                                    handleKeycapChange(
                                      'texts',
                                      {
                                        'style.letterSpacing': parseFloat(
                                          inputValue.toFixed(2)
                                        ),
                                      },
                                      selectedTextIndex
                                    );
                                  }}
                                  unitSymbol="mm"
                                  step="0.1"
                                />
                              </Form.Group>

                              <Form.Group className="form-group">
                                <Form.Label>
                                  {t('text-editor-component-word-spacing')}
                                </Form.Label>

                                <CustomInput
                                  position="top"
                                  value={resolveSetting({
                                    path: 'style.wordSpacing',
                                    defaultValue: 0,
                                    mixedReturn: '__MIXED__',
                                  })}
                                  onChange={(e) => {
                                    const number =
                                      e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                    if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                    handleKeycapChange(
                                      'texts',
                                      {
                                        'style.wordSpacing': Number(
                                          e.target.value
                                        ), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                      },
                                      selectedTextIndex
                                    );
                                  }}
                                  onBlur={(e) => {
                                    const number =
                                      e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                    if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                    const inputValue = parseFloat(
                                      e.target.value
                                    );

                                    handleKeycapChange(
                                      'texts',
                                      {
                                        'style.wordSpacing': parseFloat(
                                          inputValue.toFixed(2)
                                        ),
                                      },
                                      selectedTextIndex
                                    );
                                  }}
                                  unitSymbol="mm"
                                  step="0.1"
                                />
                              </Form.Group>
                            </div>
                          </SplitButton>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </div>

        <div
          className={`predefined-legends-container ${
            !hasAssignedKeycapIDs && 'disabled-predefined-legends-container'
          }`}
          id="predefined-legends"
        >
          <div className="predefined-legends-heading-container">
            <h3>{t('text-editor-component-predefined-legends')}</h3>

            <OverlayTrigger
              trigger="click"
              rootClose // close the popover when the user clicks outside of it
              placement="bottom"
              overlay={
                <Popover>
                  <Popover.Header as="h3">
                    {t('text-editor-component-predefined-legends-popover-1')}
                  </Popover.Header>
                  <Popover.Body>
                    {t('text-editor-component-predefined-legends-popover-2')}
                    <br />
                    <br />
                    <ul>
                      <li>
                        {t(
                          'text-editor-component-predefined-legends-popover-3'
                        )}
                      </li>
                      <li>
                        {t(
                          'text-editor-component-predefined-legends-popover-4'
                        )}
                      </li>
                      <li>
                        {t(
                          'text-editor-component-predefined-legends-popover-5'
                        )}
                      </li>
                    </ul>
                  </Popover.Body>
                </Popover>
              }
            >
              <Button className="info-popover">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="#707070"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                </svg>
              </Button>
            </OverlayTrigger>

            {screenWidthXL && (
              <Button
                variant="link"
                onClick={() =>
                  setShowPredefinedLegendsSettings(
                    !showPredefinedLegendsSettings
                  )
                }
                aria-expanded={showPredefinedLegendsSettings}
                className="toggle-settings-arrow"
              >
                {showPredefinedLegendsSettings ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-caret-up-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-caret-down-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                  </svg>
                )}
              </Button>
            )}
          </div>

          {(screenWidthXL && showPredefinedLegendsSettings) ||
          !screenWidthXL ? (
            <div className="predefined-legends-settings-container">
              {/* Language Selector */}
              <Form.Group className="form-group">
                <Form.Label>{t('text-editor-component-language')}</Form.Label>

                <Form.Select
                  className="customizer-mobile-device-compatible-top"
                  value={id}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                >
                  <option value="">
                    {t('text-editor-component-choose-language')}
                  </option>
                  {legends.map((legend) => (
                    <option key={legend._id} value={legend._id}>
                      {legend.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group
                className={`form-group ${
                  !id
                    ? 'disabled-language-settings-container'
                    : 'enabled-language-settings-container'
                }`}
              >
                <Form.Label>
                  {t('text-editor-component-operating-system')}
                </Form.Label>

                <Form.Select
                  className="customizer-mobile-device-compatible-top"
                  value={os}
                  onChange={(e) => {
                    const newValue = e.target.value;

                    if (selectedLegend) {
                      updateDesign({
                        'textEditor.predefinedLegends.os': newValue, // Update the `os` property
                        keycapData: (prevKeycapData) =>
                          applyLegends(prevKeycapData, selectedLegend.legends, {
                            configurationStyle,
                            os: newValue, // Use the new `os` value
                            legendStyle,
                          }), // applying the legends directly in the onChange, instead of using a useEffect hook, to ensure that legends are only being applied when the user changes the os setting. (a useEffect would also be triggered when the TextEditor component mounts or re-mounts, which caused unintentional behavior)
                      });
                    }
                  }}
                >
                  <option value="windows">
                    {t('text-editor-component-windows')}
                  </option>
                  <option value="mac">{t('text-editor-component-mac')}</option>
                </Form.Select>
              </Form.Group>

              <Form.Group
                className={`form-group ${
                  !id
                    ? 'disabled-language-settings-container'
                    : 'enabled-language-settings-container'
                }`}
              >
                <Form.Label>
                  {t('text-editor-component-configuration-style')}
                </Form.Label>

                <ToggleButtonGroup
                  type="radio"
                  name="configurationStyle"
                  value={configurationStyle}
                  onChange={(val) => {
                    if (selectedLegend) {
                      updateDesign({
                        'textEditor.predefinedLegends.configurationStyle': val, // Update the `configurationStyle` property
                        keycapData: (prevKeycapData) =>
                          applyLegends(prevKeycapData, selectedLegend.legends, {
                            configurationStyle: val, // Use the new `configurationStyle`
                            os,
                            legendStyle,
                          }), // applying the legends directly in the onChange, instead of using a useEffect hook, to ensure that legends are only being applied when the user changes the configurationStyle setting. (a useEffect would also be triggered when the TextEditor component mounts or re-mounts, which caused unintentional behavior)
                      });
                    }
                  }}
                >
                  <ToggleButton
                    id="full-version"
                    value="full"
                    variant="outline-secondary"
                  >
                    {t('text-editor-component-full')}
                  </ToggleButton>
                  <ToggleButton
                    id="simple-version"
                    value="simple"
                    variant="outline-secondary"
                  >
                    {t('text-editor-component-simple')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Form.Group>

              <Form.Group
                className={`form-group ${
                  !id
                    ? 'disabled-language-settings-container'
                    : 'enabled-language-settings-container'
                }`}
              >
                <Form.Label>
                  {t('text-editor-component-legend-style')}
                </Form.Label>

                <ToggleButtonGroup
                  type="radio"
                  name="legendStyle"
                  value={legendStyle}
                  onChange={(val) => {
                    if (selectedLegend) {
                      updateDesign({
                        'textEditor.predefinedLegends.legendStyle': val, // Update the `legendStyle` property
                        keycapData: (prevKeycapData) =>
                          applyLegends(prevKeycapData, selectedLegend.legends, {
                            configurationStyle,
                            os,
                            legendStyle: val, // Use the new `legendStyle`
                          }), // applying the legends directly in the onChange, instead of using a useEffect hook, to ensure that legends are only being applied when the user changes the legendStyle setting. (a useEffect would also be triggered when the TextEditor component mounts or re-mounts, which caused unintentional behavior)
                      });
                    }
                  }}
                >
                  <ToggleButton
                    id="text-legend-style"
                    value="text"
                    variant="outline-secondary"
                  >
                    {t('text-editor-component-text')}
                  </ToggleButton>
                  <ToggleButton
                    id="icon-legend-style"
                    value="icon"
                    variant="outline-secondary"
                  >
                    {t('text-editor-component-icon')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Form.Group>
            </div>
          ) : (
            <div></div>
          )}
        </div>
      </div>
    );
  }
);

export default TextEditor;
